<template>
  <div class="us-page">
    <q-linear-progress v-if="this.isFetching" indeterminate color="primary" class="us-page__progress" />

    <div class="us-page__header">
      <div class="us-page__header-main">
        <h1 class="us-page__title">Журнал документов</h1>
        <q-tabs v-model="this.activeTab"
          align="left"
          dense
          shrink
          no-caps
          active-color="primary"
          indicator-color="primary"
          class="us-tabs"
          content-class="us-tabs__scroll"
          @update:model-value="this.onTabChange">
          <q-tab v-for="tab in this.computedTabs" :key="tab.name" :name="tab.name" :label="tab.label" />
        </q-tabs>
      </div>
      <div class="us-page__header-actions">
        <q-btn
          flat round
          :icon="this.showServiceFields ? 'code_off' : 'code'"
          :color="this.showServiceFields ? 'deep-orange' : 'grey-6'"
          @click="this.showServiceFields = !this.showServiceFields"
        >
          <q-tooltip>{{ this.showServiceFields ? 'Скрыть служебные поля' : 'Показать служебные поля (ID)' }}</q-tooltip>
        </q-btn>
        <q-btn flat round icon="refresh" color="primary" :loading="this.isFetching" @click="this.loadCurrentTab" />
      </div>
    </div>

    <div v-if="this.isDocTabActive" class="us-doc-controls-band">
      <div class="us-doc-filters-row">
        <div class="us-doc-filters">
          <q-select
            :model-value="this.doc.filters.resident_id"
            :options="this.docFilteredResidentOpts"
            label="ЖК"
            dense outlined clearable
            use-input hide-selected fill-input input-debounce="200"
            class="cd-filter cd-filter--sm"
            emit-value map-options
            :loading="this.docFiltersLoading"
            @filter="this.filterDocResidents"
            @input-value="this.onDocResidentInputValueUpdate"
            @update:model-value="this.onDocResidentChange"
          />

          <q-select
            :model-value="this.doc.filters.project_type_id"
            :options="this.docProjectTypeOptions"
            label="Тип проекта"
            dense outlined clearable
            class="cd-filter cd-filter--sm"
            emit-value map-options
            :disable="!this.doc.filters.resident_id || !this.docProjectTypeOptions.length"
            :loading="this.docFiltersLoading"
            @update:model-value="this.onDocProjectTypeChange"
          />

          <q-select
            :model-value="this.doc.filters.contractor_id"
            :options="this.docFilteredContractorOpts"
            label="Контрагент"
            dense outlined clearable
            use-input hide-selected fill-input input-debounce="200"
            class="cd-filter cd-filter--sm"
            emit-value map-options
            :disable="!this.doc.filters.resident_id || !this.docContractorOptions.length"
            @filter="this.filterDocContractors"
            @update:model-value="this.onDocContractorChange"
          />

          <q-select
            :model-value="this.doc.filters.project_entrance_id"
            :options="this.docEntranceOptions"
            label="Подъезд и Блок"
            dense outlined clearable
            class="cd-filter cd-filter--entrance"
            emit-value map-options
            option-value="project_entrance_id"
            option-label="entrance_name"
            :disable="!this.doc.filters.resident_id || !this.doc.filters.project_type_id"
            :loading="this.docEntrancesLoading"
            @update:model-value="this.onDocProjectEntranceChange"
          />

          <q-select
            :model-value="this.doc.filters.level_num"
            :options="this.docLevelOptions"
            label="Этаж"
            dense outlined clearable
            class="cd-filter cd-filter--level"
            emit-value map-options
            :disable="!this.doc.filters.resident_id || !this.docLevelOptions.length"
            @update:model-value="(v) => this.onDocFilter('level_num', v)"
          />

          <q-select
            ref="docAuditSelect"
            class="cd-filter cd-filter--md"
            outlined
            dense
            multiple
            emit-value
            map-options
            behavior="menu"
            options-dense
            popup-content-class="us-doc-audit-popup"
            dropdown-icon="img:/img/icons/arrow_down.svg"
            label="Подписание и ЭСФ"
            option-value="value"
            option-label="label"
            :options="this.docAuditStatusOptions"
            :model-value="this.docAuditMultiTags"
            :display-value="this.docAuditMultiDisplay"
            @update:model-value="this.onDocAuditMultiChange"
          >
            <template #option="scope">
              <q-item
                v-bind="scope.itemProps"
                :class="[
                  'us-doc-audit-option',
                  scope.selected ? 'us-doc-audit-option--selected' : '',
                  String(scope.opt?.value || '').startsWith('esf_')
                    ? 'us-doc-audit-option--esf'
                    : 'us-doc-audit-option--sign',
                  scope.opt?.value === 'esf_no' ? 'us-doc-audit-option--esf-group-end' : '',
                  scope.opt?.value === 'sign_signed' ? 'us-doc-audit-option--sign-group-start' : '',
                ]"
              >
                <q-item-section side>
                  <q-icon
                    :name="scope.selected ? 'check_circle' : 'radio_button_unchecked'"
                    :class="[
                      'us-doc-audit-option__icon',
                      scope.selected ? 'us-doc-audit-option__icon--selected' : '',
                      String(scope.opt?.value || '').startsWith('esf_')
                        ? 'us-doc-audit-option__icon--esf'
                        : 'us-doc-audit-option__icon--sign',
                    ]"
                    size="20px"
                  />
                </q-item-section>
                <q-item-section>
                  <q-item-label class="us-doc-audit-option__label">{{ scope.opt.label }}</q-item-label>
                </q-item-section>
              </q-item>
            </template>
          </q-select>

          <q-input
            v-if="this.activeTab === 'exec_docs'"
            :model-value="this.doc.filters.doc_date_from"
            label="Дата с"
            outlined dense clearable
            type="date"
            hide-bottom-space
            class="cd-filter cd-filter--level"
            @update:model-value="(v) => this.onDocFilter('doc_date_from', v || null)"
            @clear="this.onDocFilter('doc_date_from', null)"
          />

          <q-input
            v-if="this.activeTab === 'exec_docs'"
            :model-value="this.doc.filters.doc_date_to"
            label="Дата по"
            outlined dense clearable
            type="date"
            hide-bottom-space
            class="cd-filter cd-filter--level"
            @update:model-value="(v) => this.onDocFilter('doc_date_to', v || null)"
            @clear="this.onDocFilter('doc_date_to', null)"
          />
        </div>

        <q-btn
          outline dense no-caps icon="filter_alt_off" color="grey-8"
          class="us-doc-reset-btn"
          @click="this.resetDocFilters"
        >
          <q-tooltip>Сбросить фильтры</q-tooltip>
        </q-btn>
      </div>
    </div>

    <div class="us-shell">
      <q-tab-panels v-model="this.activeTab" animated keep-alive class="us-panels">

            <!-- ===== ВКЛАДКА 1: НАКЛАДНЫЕ ===== -->
      <q-tab-panel name="invoices" class="us-tab-panel">
        <div class="us-filters-shell">
        <div class="us-filters-shell__title">Фильтры</div>
        <div class="us-filters">
          <q-input :model-value="this.inv.projectLabel" readonly outlined dense placeholder="Проект УСП"
            class="us-filters__item us-filters__item--wide" clearable @clear="this.clearInvProject">
            <template #prepend>
              <q-btn icon="list" flat dense round @click="this.openUspDialog('inv')">
                <q-tooltip>Выбрать проект УСП</q-tooltip>
              </q-btn>
            </template>
          </q-input>
          <div class="us-filters__item">
            <CustomSelect :options="this.projectTypesOptions" label="Тип проекта"
              option-label-key="project_type_name" option-value-key="project_type_id"
              :value="this.inv.filters.project_type_id"
              @change="(v) => this.onInvFilter('project_type_id', v)" />
          </div>
          <div class="us-filters__item">
            <CustomSelect :options="this.providersOptions" label="Поставщик"
              option-label-key="provider_name" option-value-key="provider_id"
              :value="this.inv.filters.provider_id"
              @change="(v) => this.onInvFilter('provider_id', v)" />
          </div>
          <div class="us-filters__item us-filters__item--narrow">
            <CustomSelect :options="this.signedOptions" label="Статус"
              option-label-key="label" option-value-key="value"
              :value="this.inv.filters.is_signed"
              @change="(v) => this.onInvFilter('is_signed', v)" />
          </div>
          <div class="us-filters__item us-filters__item--narrow">
            <CustomSelect :options="this.sendTo1cOptions" label="Отправка в 1С"
              option-label-key="label" option-value-key="value"
              :value="this.inv.filters.is_send_to_1c"
              @change="(v) => this.onInvFilter('is_send_to_1c', v)" />
          </div>
          <div class="us-filters__item us-filters__item--narrow">
            <CustomSelect :options="this.manualOptions" label="Тип накладной"
              option-label-key="label" option-value-key="value"
              :value="this.inv.filters.is_manual_signed"
              @change="(v) => this.onInvFilter('is_manual_signed', v)" />
          </div>
          <q-input :model-value="this.inv.filters.date_from" outlined dense placeholder="Дата с"
            class="us-filters__item us-filters__item--date" clearable
            @clear="this.onInvFilter('date_from', null)">
            <template #append>
              <q-icon name="event" class="cursor-pointer">
                <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                  <q-date :model-value="this.inv.filters.date_from" mask="YYYY-MM-DD" minimal
                    @update:model-value="(v) => { this.onInvFilter('date_from', v) }" />
                </q-popup-proxy>
              </q-icon>
            </template>
          </q-input>
          <q-input :model-value="this.inv.filters.date_to" outlined dense placeholder="Дата по"
            class="us-filters__item us-filters__item--date" clearable
            @clear="this.onInvFilter('date_to', null)">
            <template #append>
              <q-icon name="event" class="cursor-pointer">
                <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                  <q-date :model-value="this.inv.filters.date_to" mask="YYYY-MM-DD" minimal
                    @update:model-value="(v) => { this.onInvFilter('date_to', v) }" />
                </q-popup-proxy>
              </q-icon>
            </template>
          </q-input>
          <q-btn outline dense no-caps icon="filter_alt_off" color="grey-8"
            class="us-filters__btn us-reset-btn" @click="this.resetInvFilters">
            <q-tooltip>Сбросить фильтры</q-tooltip>
          </q-btn>
        </div>
        </div>

        <div class="us-table-card">
          <div class="us-journal-grid-head">
            <div class="us-journal-grid-head__cell">Проект</div>
            <div class="us-journal-grid-head__cell">Контрагент</div>
            <div class="us-journal-grid-head__cell">Документы</div>
          </div>
          <div class="us-doc-list">
            <q-inner-loading :showing="this.inv.loading">
              <q-spinner color="primary" size="34px" />
            </q-inner-loading>

            <div v-if="!this.inv.loading && !this.inv.data.length" class="us-empty-state">
              Нет данных
            </div>

            <div v-for="row in this.inv.data" :key="row.provider_request_invoice_id" class="us-row-card">
              <div class="us-row-card__head">
                <div class="us-row-card__title-wrap">
                  <h3 class="us-row-card__title">Накладная № {{ row.invoice_num || row.provider_request_invoice_id }}</h3>
                  <span v-if="this.showServiceFields" class="us-id-chip">ID: {{ row.provider_request_invoice_id }}</span>
                </div>
                <span class="us-row-card__date">{{ row.invoice_date || "Без даты" }}</span>
              </div>

              <div class="us-row-grid">
                <div class="us-grid-col">
                  <div class="us-grid-col__label">Проект</div>
                  <a :href="buildProjectLink(row, 'M__ProjectFormMaterialTab')" target="_blank" rel="noopener" class="proj-link">
                    {{ row.sbs_project_name || row.project_name }}
                  </a>
                  <div class="us-meta-tags">
                    <span v-if="row.project_type_name" class="us-meta-tag">{{ row.project_type_name }}</span>
                    <span v-if="row.block_name" class="us-meta-tag">Блок {{ row.block_name }}</span>
                  </div>
                </div>

                <div class="us-grid-col">
                  <div class="us-grid-col__label">Контрагент</div>
                  <div class="doc-cell-contractor">
                    <template v-if="this.invoiceCounterpartyNames(row).length">
                      <span
                        v-for="(name, idx) in this.invoiceCounterpartyNames(row)"
                        :key="`${row.provider_request_invoice_id}_${idx}_${name}`"
                        class="doc-contractor-name"
                      >
                        {{ name }}
                      </span>
                    </template>
                    <span v-else class="doc-contractor-empty">—</span>
                  </div>
                </div>

                <div class="us-grid-col">
                  <div class="us-grid-col__label">Документы</div>
                  <article
                    :class="[
                      'us-doc-mini-card',
                      row.is_manual_signed ? 'us-doc-mini-card--beige' : row.can_sign ? 'us-doc-mini-card--blue' : 'us-doc-mini-card--green'
                    ]"
                  >
                    <div class="us-doc-mini-card__head">
                      <div>
                        <h4 class="us-doc-mini-card__title">Накладная</h4>
                        <p class="us-doc-mini-card__date">{{ row.invoice_date || "Дата не указана" }}</p>
                      </div>
                      <div class="us-doc-mini-card__badges">
                        <span :class="['us-badge', row.sign_status === 'SIGNED' ? 'us-badge--signed' : 'us-badge--muted']">
                          {{ row.sign_status === 'SIGNED' ? 'Подписано' : this.invSignStatusText(row) }}
                        </span>
                        <span v-if="row.is_send_to_1c === 2" class="us-badge us-badge--esf">{{ '1' + '\u0421' }}</span>
                      </div>
                    </div>

                    <div class="us-info-block">
                      <div v-if="row.invoice_sum" class="us-info-line">
                        Сумма: {{ Number(row.invoice_sum).toLocaleString("ru-RU") }} ₸
                      </div>
                      <div v-if="row.materials_count" class="us-info-line us-info-line--clickable" @click.stop="this.openInvMaterialsModal(row)">
                        Материалы: {{ row.materials_count }}
                      </div>
                      <div class="us-info-line">
                        Тип: {{ row.is_manual_signed ? "Ручная" : "Электронная" }}
                      </div>
                    </div>

                    <div v-if="this.showServiceFields" class="us-info-block us-info-block--service">
                      <div class="us-info-line">[floor_map_document_id]: {{ row.floor_map_document_id ?? '\u2014' }}</div>
                      <div class="us-info-line">[project_entrance_id]: {{ row.project_entrance_id ?? '\u2014' }}</div>
                      <div class="us-info-line">[project_id]: {{ row.project_id ?? '\u2014' }}</div>
                      <div class="us-info-line">[sbs_project_id]: {{ row.sbs_project_id ?? '\u2014' }}</div>
                    </div>

                    <div v-if="(row.parties || []).length" class="doc-signers doc-signers--in-card">
                      <div
                        v-for="party in (row.parties || [])"
                        :key="`${row.provider_request_invoice_id}_${party.sign_type_code}`"
                        :class="['doc-signer', party.sign_status === 'SIGNED' ? 'doc-signer--done' : 'doc-signer--waiting', party.is_me ? 'doc-signer--me' : '']"
                      >
                        <q-icon
                          :name="party.sign_status === 'SIGNED' ? 'check_circle' : party.sign_status === 'FAILED' ? 'error_outline' : 'schedule'"
                          :class="['doc-signer__icon', party.sign_status === 'SIGNED' ? 'doc-signer__icon--done' : 'doc-signer__icon--wait']"
                          size="15px"
                        />
                        <div class="doc-signer__info">
                          <span class="doc-signer__role">{{ party.sign_role }}</span>
                          <span :class="['doc-signer__fio', party.sign_status === 'SIGNED' ? 'doc-signer__fio--done' : '']">{{ party.person_name || "—" }}</span>
                          <span v-if="party.sign_status === 'SIGNED' && party.sign_date" class="doc-signer__date">
                            <q-icon name="event_available" size="10px" /> {{ party.sign_date }}
                          </span>
                        </div>
                        <span v-if="party.is_me" class="doc-signer__me-badge">Я</span>
                      </div>
                    </div>

                    <div v-if="!row.is_manual_signed" class="us-doc-mini-card__actions">
                      <q-btn
                        v-if="row.can_sign"
                        unelevated
                        no-caps
                        class="us-sign-btn"
                        :loading="!!this.invSigning[row.provider_request_invoice_id]"
                        @click.stop="this.onSignInvoice(row)"
                        label="Подписать"
                      />
                      <span v-else :class="['us-badge', row.sign_status === 'SIGNED' ? 'us-badge--signed' : 'us-badge--muted']">
                        {{ this.invSignStatusText(row) }}
                      </span>

                      <q-btn
                        flat
                        dense
                        round
                        icon="picture_as_pdf"
                        color="primary"
                        size="sm"
                        :loading="!!this.invDownloading[row.provider_request_invoice_id]"
                        @click.stop="this.onDownloadInvoice(row)"
                      >
                        <q-tooltip>Открыть PDF</q-tooltip>
                      </q-btn>

                      <q-btn
                        v-if="!row.is_provider_user && this.getInv1CLabel(row) && (row.is_send_to_1c === 2 || (row.contractor_sign_status === 'SIGNED' && row.provider_sign_status === 'SIGNED'))"
                        unelevated
                        no-caps
                        class="us-ghost-btn"
                        :disable="row.is_send_to_1c === 2"
                        :loading="!!this.invSending1C[row.provider_request_invoice_id]"
                        @click.stop="this.onSendInvoiceTo1C(row)"
                      >
                        {{ row.is_send_to_1c === 2 ? "Отправлено в 1С" : this.getInv1CLabel(row) }}
                      </q-btn>
                    </div>
                  </article>
                </div>
              </div>
            </div>
          </div>

          <div v-if="this.inv.pages > 1" class="us-pagination">
            <q-pagination
              :model-value="this.inv.page"
              :max="this.inv.pages"
              max-pages="7"
              dense
              outline
              direction-links
              color="primary"
              size="sm"
              @update:model-value="this.onInvPageChange"
            />
          </div>
        </div>
      </q-tab-panel>

      <!-- ===== ВКЛАДКА 2: ДОГОВОРА ===== -->
      <q-tab-panel name="agreements" class="us-tab-panel">
        <div class="us-filters-shell">
        <div class="us-filters-shell__title">Фильтры</div>
        <div class="us-filters">
          <q-input :model-value="this.agr.projectLabel" readonly outlined dense placeholder="Проект УСП"
            class="us-filters__item us-filters__item--wide" clearable @clear="this.clearAgrProject">
            <template #prepend>
              <q-btn icon="list" flat dense round @click="this.openUspDialog('agr')">
                <q-tooltip>Выбрать проект УСП</q-tooltip>
              </q-btn>
            </template>
          </q-input>
          <div class="us-filters__item">
            <CustomSelect :options="this.projectTypesOptions" label="Тип проекта"
              option-label-key="project_type_name" option-value-key="project_type_id"
              :value="this.agr.filters.project_type_id"
              @change="(v) => this.onAgrFilter('project_type_id', v)" />
          </div>
          <div class="us-filters__item">
            <CustomSelect :options="this.agrContractorsOptions" label="Контрагент"
              option-label-key="contractor_name" option-value-key="contractor_id"
              :value="this.agr.filters.contractor_id"
              @change="(v) => this.onAgrFilter('contractor_id', v)" />
          </div>
          <div class="us-filters__item us-filters__item--narrow">
            <CustomSelect :options="this.signedOptions" label="Статус"
              option-label-key="label" option-value-key="value"
              :value="this.agr.filters.is_signed"
              @change="(v) => this.onAgrFilter('is_signed', v)" />
          </div>
          <q-btn outline dense no-caps icon="filter_alt_off" color="grey-8"
            class="us-filters__btn us-reset-btn" @click="this.resetAgrFilters">
            <q-tooltip>Сбросить фильтры</q-tooltip>
          </q-btn>
        </div>
        </div>

        <div class="us-table-card">
          <div class="us-journal-grid-head">
            <div class="us-journal-grid-head__cell">Проект</div>
            <div class="us-journal-grid-head__cell">Контрагент</div>
            <div class="us-journal-grid-head__cell">Документы</div>
          </div>
          <div class="us-doc-list">
            <q-inner-loading :showing="this.agr.loading">
              <q-spinner color="primary" size="34px" />
            </q-inner-loading>

            <div v-if="!this.agr.loading && !this.agr.data.length" class="us-empty-state">
              Нет данных
            </div>

            <div v-for="row in this.agr.data" :key="row.project_agreement_id" class="us-row-card">
              <div class="us-row-card__head">
                <div class="us-row-card__title-wrap">
                  <h3 class="us-row-card__title">Договор № {{ row.agreement_num || row.project_agreement_id }}</h3>
                  <span v-if="this.showServiceFields" class="us-id-chip">ID: {{ row.project_agreement_id }}</span>
                </div>
                <span class="us-row-card__date">{{ row.document_date || "Без даты" }}</span>
              </div>

              <div class="us-row-grid">
                <div class="us-grid-col">
                  <div class="us-grid-col__label">Проект</div>
                  <a :href="buildProjectLink(row, 'M__ProjectFormInfoTab')" target="_blank" rel="noopener" class="proj-link">
                    {{ row.sbs_project_name || row.project_name }}
                  </a>
                  <div class="us-meta-tags">
                    <span v-if="row.project_type_name" class="us-meta-tag">{{ row.project_type_name }}</span>
                    <span v-if="row.blocks" class="us-meta-tag">Блоки: {{ row.blocks }}</span>
                  </div>
                </div>

                <div class="us-grid-col">
                  <div class="us-grid-col__label">Контрагент</div>
                  <div class="doc-cell-contractor">
                    <span v-if="row.contractor_name" class="doc-contractor-name">{{ row.contractor_name }}</span>
                    <span v-else class="doc-contractor-empty">—</span>
                  </div>
                </div>

                <div class="us-grid-col">
                  <div class="us-grid-col__label">Документы</div>
                  <article :class="['us-doc-mini-card', row.all_signed ? 'us-doc-mini-card--green' : 'us-doc-mini-card--beige']">
                    <div class="us-doc-mini-card__head">
                      <div>
                        <h4 class="us-doc-mini-card__title">Договор проекта</h4>
                        <p class="us-doc-mini-card__date">{{ row.document_date || "Дата не указана" }}</p>
                      </div>
                      <div class="us-doc-mini-card__badges">
                        <span :class="['us-badge', row.all_signed ? 'us-badge--signed' : 'us-badge--muted']">
                          {{ row.all_signed ? "Подписано" : "Ожидает подписи" }}
                        </span>
                      </div>
                    </div>

                    <div class="us-info-block">
                      <div v-if="row.contractor_agreement_list_type_code" class="us-info-line">
                        Тип: {{ agrTypeLabel(row.contractor_agreement_list_type_code) }}
                      </div>
                      <div v-if="row.is_sent_to_1c === 1" class="us-info-line">Отправлено в 1С</div>
                      <div v-if="row.kod_bk" class="us-info-line">Код 1С: {{ row.kod_bk }}</div>
                    </div>

                    <div v-if="this.showServiceFields" class="us-info-block us-info-block--service">
                      <div class="us-info-line">[floor_map_document_id]: {{ row.floor_map_document_id ?? '\u2014' }}</div>
                      <div class="us-info-line">[project_entrance_id]: {{ row.project_entrance_id ?? '\u2014' }}</div>
                      <div class="us-info-line">[project_id]: {{ row.project_id ?? '\u2014' }}</div>
                      <div class="us-info-line">[sbs_project_id]: {{ row.sbs_project_id ?? '\u2014' }}</div>
                    </div>

                    <div v-if="(row.signers || []).length" class="doc-signers doc-signers--in-card">
                      <div
                        v-for="signer in (row.signers || [])"
                        :key="`${row.project_agreement_id}_${signer.assign_type_code}`"
                        :class="['doc-signer', signer.is_signed ? 'doc-signer--done' : 'doc-signer--waiting', signer.is_me ? 'doc-signer--me' : '']"
                      >
                        <q-icon :name="signer.is_signed ? 'check_circle' : 'schedule'" :class="['doc-signer__icon', signer.is_signed ? 'doc-signer__icon--done' : 'doc-signer__icon--wait']" size="15px" />
                        <div class="doc-signer__info">
                          <span class="doc-signer__role">{{ signer.sign_role }}</span>
                          <span :class="['doc-signer__fio', signer.is_signed ? 'doc-signer__fio--done' : '']">{{ signer.person_fio }}</span>
                          <span v-if="signer.company_name" class="doc-signer__company">{{ signer.company_name }}</span>
                          <span v-if="signer.is_signed && signer.sign_date" class="doc-signer__date">
                            <q-icon name="event_available" size="10px" /> {{ signer.sign_date }}
                          </span>
                        </div>
                        <span v-if="signer.is_me" class="doc-signer__me-badge">Я</span>
                      </div>
                    </div>

                    <div class="us-doc-mini-card__actions">
                      <q-btn
                        v-if="row.is_me_unsigned"
                        unelevated
                        no-caps
                        class="us-sign-btn"
                        :loading="agrSigning[row.project_agreement_id]"
                        :disable="agrSigning[row.project_agreement_id]"
                        @click="onSignAgreement(row)"
                        label="Подписать"
                      />

                      <q-btn
                        flat
                        dense
                        round
                        icon="picture_as_pdf"
                        color="primary"
                        size="sm"
                        :loading="agrDownloading[row.project_agreement_id]"
                        :disable="agrDownloading[row.project_agreement_id]"
                        @click="onDownloadAgreement(row)"
                      >
                        <q-tooltip>Открыть PDF</q-tooltip>
                      </q-btn>
                    </div>
                  </article>
                </div>
              </div>
            </div>
          </div>

          <div v-if="this.agr.pages > 1" class="us-pagination">
            <q-pagination
              :model-value="this.agr.page"
              :max="this.agr.pages"
              max-pages="7"
              dense
              outline
              direction-links
              color="primary"
              size="sm"
              @update:model-value="this.onAgrPageChange"
            />
          </div>
        </div>
      </q-tab-panel>

      <!-- ===== ВКЛАДКИ 1-2: БУХ ДОКУМЕНТАЦИЯ / ИСПОЛНИТЕЛЬНАЯ ДОКУМЕНТАЦИЯ ===== -->
      <template v-for="docTabName in ['buh_docs', 'exec_docs']" :key="docTabName">
      <q-tab-panel :name="docTabName" class="us-tab-panel us-tab-panel--docs">
        <div class="us-table-card us-table-card--docs">
          <div class="us-table-scroll-host">
          <div v-if="docTabName === 'buh_docs'" class="us-journal-grid-head us-journal-grid-head--buh">
            <div class="us-journal-grid-head__cell us-journal-grid-head__cell--project">
              <div class="us-select-all-head">
                <q-checkbox
                  dense
                  :model-value="this.docAllSelected"
                  :indeterminate="this.docIndeterminate"
                  :disable="!this.docCurrent.data.some(r => r.can_sign)"
                  label="Выбрать все"
                  @update:model-value="this.toggleDocSelectAll"
                />
                <span v-if="this.docSelectedIds.length > 0" class="us-count us-count--head text-primary">
                  Выбрано: {{ this.docSelectedIds.length }}
                </span>
              </div>
              <span class="us-journal-grid-head__title">Проект</span>
            </div>
            <div class="us-journal-grid-head__cell us-journal-grid-head__cell--contractor">Контрагент</div>
            <div class="us-journal-grid-head__cell us-journal-grid-head__cell--docs">Документы</div>
            <div class="us-journal-grid-head__doc-types">
              <div class="us-journal-grid-head__doc-type us-journal-grid-head__doc-type--blue">КС-2В</div>
              <div class="us-journal-grid-head__doc-type us-journal-grid-head__doc-type--beige">АВР</div>
              <div class="us-journal-grid-head__doc-type us-journal-grid-head__doc-type--green">3КС</div>
            </div>
          </div>
          <div v-else class="us-journal-grid-head">
            <div class="us-journal-grid-head__cell us-journal-grid-head__cell--project">
              <div class="us-select-all-head">
                <q-checkbox
                  dense
                  :model-value="this.docAllSelected"
                  :indeterminate="this.docIndeterminate"
                  :disable="!this.docCurrent.data.some(r => r.can_sign)"
                  label="Выбрать все"
                  @update:model-value="this.toggleDocSelectAll"
                />
                <span v-if="this.docSelectedIds.length > 0" class="us-count us-count--head text-primary">
                  Выбрано: {{ this.docSelectedIds.length }}
                </span>
              </div>
              <span class="us-journal-grid-head__title">Проект</span>
            </div>
            <div class="us-journal-grid-head__cell">Контрагент</div>
            <div class="us-journal-grid-head__cell">Документы</div>
          </div>

          <div v-if="this.docSelectedIds.length > 0" class="us-table-toolbar">
            <q-space />
            <q-btn
              unelevated
              size="sm"
              color="positive"
              icon="verified"
              label="Массовое подписание"
              class="us-batch-sign-btn"
              :loading="this.docBatchSigning"
              :disable="this.docSelectedIds.length > 10"
              @click="this.onBatchSignDocuments"
            >
              <q-tooltip v-if="this.docSelectedIds.length > 10">Максимум 10 документов</q-tooltip>
            </q-btn>
          </div>

          <div class="us-doc-list">
            <q-inner-loading :showing="this.doc.tabs[docTabName].loading">
              <q-spinner color="primary" size="34px" />
            </q-inner-loading>

            <div v-if="!this.doc.tabs[docTabName].loading && !this.doc.tabs[docTabName].data.length" class="us-empty-state">
              Нет данных
            </div>

            <template v-if="docTabName === 'buh_docs'">
              <template v-for="group in this.buhDocCallGroups" :key="group.key">
                <div v-if="group.help_call_id != null" class="us-call-group">
                  <q-icon name="call" size="14px" class="q-mr-xs" />
                  Вызов #{{ group.help_call_id }}
                </div>

                <div class="us-row-card us-row-card--buh">
                  <div class="us-row-grid us-row-grid--buh">
                    <div class="us-grid-col">
                      <div class="us-grid-col__label">Проект</div>
                      <a :href="buildProjectLink(group.row, 'M__ProjectFormDocumentTab')" target="_blank" rel="noopener" class="proj-link">
                        {{ group.row.sbs_project_name || group.row.project_name }}
                      </a>
                      <div class="us-meta-tags">
                        <span v-if="group.row.project_type_name" class="us-meta-tag">{{ group.row.project_type_name }}</span>
                        <span v-if="group.row.block_name" class="us-meta-tag">Блок {{ group.row.block_name }}</span>
                        <span v-if="group.row.project_entrance_name || group.row.entrance_name" class="us-meta-tag">
                          Подъезд {{ group.row.project_entrance_name || group.row.entrance_name }}
                        </span>
                        <span v-if="group.row.floors" class="us-meta-tag">Этажи {{ group.row.floors }}</span>
                      </div>
                      <div v-if="group.worksText" class="us-project-works">
                        <span class="us-project-works__label">Работы:</span>
                        <span class="us-project-works__value">{{ group.worksText }}</span>
                      </div>
                      <div v-if="this.isBuhGroupDeletable(group)" class="us-buh-group-actions">
                        <q-btn
                          unelevated
                          no-caps
                          outline
                          size="sm"
                          color="negative"
                          icon="delete"
                          label="Удалить"
                          :loading="!!this.docDeleting[group.row.floor_map_document_id]"
                          @click.stop="this.onDeleteBuhGroup(group)"
                        />
                      </div>
                    </div>

                    <div class="us-grid-col">
                      <div class="us-grid-col__label">Контрагент</div>
                      <div class="doc-cell-contractor">
                        <span v-if="group.contractor_name" class="doc-contractor-name">{{ group.contractor_name }}</span>
                        <span v-else class="doc-contractor-empty">—</span>
                      </div>
                    </div>

                    <div class="us-grid-col us-grid-col--docs">
                      <div class="us-doc-card-grid us-doc-card-grid--buh">
                        <article
                          v-for="card in group.cards"
                          :key="card.key"
                          :class="['us-doc-mini-card', 'us-doc-mini-card--buh', card.toneClass, card.row ? '' : 'us-doc-mini-card--placeholder']"
                        >
                          <div v-if="card.row" class="us-doc-mini-card__head">
                            <div class="us-doc-mini-card__title-wrap">
                              <q-checkbox
                                v-if="card.row.can_sign"
                                dense
                                :model-value="this.docSelectedIds.includes(card.row.floor_map_document_id)"
                                @update:model-value="(v) => this.toggleDocSelectItem(card.row.floor_map_document_id, v)"
                              />
                              <div>
                                <h4 class="us-doc-mini-card__title">{{ this.prettyDocTypeLabel(card.row.doc_type, card.label) }}</h4>
                                <p class="us-doc-mini-card__date">{{ card.row.doc_create_date || "Дата не указана" }}</p>
                              </div>
                            </div>
                            <div class="us-doc-mini-card__badges">
                              <span v-if="card.row.all_signed" class="us-badge us-badge--signed">Подписано</span>
                              <span v-else-if="card.row.can_sign" class="us-badge us-badge--muted">Требует подписи</span>
                              <span v-else class="us-badge us-badge--muted">Не подписан</span>
                              <span
                                v-if="showEsfBadgeInDocJournal(card.row)"
                                :class="['us-badge', hasEsfInfo(card.row) ? 'us-badge--esf' : 'us-badge--esf-no']"
                                :title="esfTooltipText(card.row)"
                              >{{ esfBadgeLabel(card.row) }}</span>
                              <q-btn
                                flat
                                dense
                                round
                                icon="picture_as_pdf"
                                color="primary"
                                size="sm"
                                :loading="!!this.docDownloading[card.row.floor_map_document_id]"
                                @click.stop="this.onDownloadDocument(card.row)"
                              >
                                <q-tooltip>Открыть PDF</q-tooltip>
                              </q-btn>
                              <q-btn
                                v-if="card.slotKey === 'buhavr'"
                                unelevated
                                no-caps
                                dense
                                class="us-k2sv-download-btn"
                                icon="picture_as_pdf"
                                label="КС-2В"
                                :loading="!!this.docDownloading[`k2sv_${card.row.floor_map_document_id}`]"
                                @click.stop="this.onDownloadK2svByBuhAvr(card.row)"
                              >
                                <q-tooltip>Открыть КС-2В этого вызова</q-tooltip>
                              </q-btn>
                            </div>
                          </div>

                          <div v-else class="us-doc-mini-card__head">
                            <div>
                              <h4 class="us-doc-mini-card__title">{{ card.label }}</h4>
                              <p class="us-doc-mini-card__date">Нет документа</p>
                            </div>
                          </div>

                          <div
                            v-if="card.row && (card.row.avr_sum || card.row.is_avr_sent_bi || this.showServiceFields)"
                            class="us-info-block"
                          >
                            <div v-if="card.row.avr_sum" class="us-info-line">
                              Сумма: {{ Number(card.row.avr_sum).toLocaleString("ru-RU") }} ₸
                            </div>
                            <div v-if="card.row.is_avr_sent_bi" class="us-info-line">Отправлено в 1С</div>
                            <div v-if="this.showAvrStornoButton(card.row)" class="us-doc-mini-card__actions us-doc-mini-card__actions--inline">
                              <q-btn
                                unelevated
                                no-caps
                                outline
                                color="deep-orange"
                                class="us-storno-btn"
                                label="Сторнировать"
                                :loading="!!this.docStornoing[card.row.floor_map_document_id]"
                                @click.stop="this.onStornoDocument(card.row)"
                              />
                            </div>
                            <div v-if="this.showServiceFields" class="us-info-line">[floor_map_document_id]: {{ card.row.floor_map_document_id }}</div>
                            <div v-if="this.showServiceFields" class="us-info-line">[project_entrance_id]: {{ card.row.project_entrance_id ?? '\u2014' }}</div>
                            <div v-if="this.showServiceFields" class="us-info-line">[project_id]: {{ card.row.project_id ?? '\u2014' }}</div>
                            <div v-if="this.showServiceFields" class="us-info-line">[sbs_project_id]: {{ card.row.sbs_project_id ?? '\u2014' }}</div>
                          </div>

                          <div v-if="card.row && (card.row.signers || []).length" class="doc-signers doc-signers--in-card">
                            <div
                              v-for="(signer, signerIndex) in (card.row.signers || [])"
                              :key="`${card.row.floor_map_document_id}_${signer.assign_type_code || 'role'}_${signer.person_fio || signer.person_name || signerIndex}_${signer.sign_date || ''}`"
                              :class="['doc-signer', signer.is_signed ? 'doc-signer--done' : 'doc-signer--waiting', signer.is_me ? 'doc-signer--me' : '']"
                            >
                              <q-icon :name="signer.is_signed ? 'check_circle' : 'schedule'" :class="['doc-signer__icon', signer.is_signed ? 'doc-signer__icon--done' : 'doc-signer__icon--wait']" size="15px" />
                              <div class="doc-signer__info">
                                <span class="doc-signer__role">{{ signer.sign_role }}</span>
                                <span :class="['doc-signer__fio', signer.is_signed ? 'doc-signer__fio--done' : '']">{{ signer.person_fio || signer.person_name || "\u2014" }}</span>
                                <span v-if="signer.is_signed && signer.sign_date" class="doc-signer__date">
                                  <q-icon name="event_available" size="10px" /> {{ signer.sign_date }}
                                </span>
                              </div>
                              <span v-if="signer.is_me" class="doc-signer__me-badge">Я</span>
                            </div>
                          </div>

                          <div v-if="card.row && card.row.can_sign" class="us-doc-mini-card__actions">
                            <q-btn
                              v-if="card.row.can_sign"
                              unelevated
                              no-caps
                              class="us-sign-btn"
                              :loading="!!this.docSigning[card.row.floor_map_document_id]"
                              @click.stop="this.onSignDocument(card.row)"
                              label="Подписать"
                            />
                          </div>

                          <div v-if="card.row && showK2svProrabCheckedLine(card.row)" class="doc-prorab-k2sv-line">
                            <q-icon name="verified" size="12px" class="q-mr-xs" color="positive" />
                            Согласовано прорабом<span v-if="card.row.prorab_k2sv_checked_fio">: {{ card.row.prorab_k2sv_checked_fio }}</span><span v-if="card.row.prorab_k2sv_checked_at">, {{ card.row.prorab_k2sv_checked_at }}</span>
                          </div>
                        </article>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </template>

            <template v-else>
              <template v-for="(row, idx) in this.doc.tabs[docTabName].data" :key="row.floor_map_document_id">
                <div
                  v-if="row.help_call_id != null && (idx === 0 || row.help_call_id !== this.doc.tabs[docTabName].data[idx - 1]?.help_call_id)"
                  class="us-call-group"
                >
                  <q-icon name="call" size="14px" class="q-mr-xs" />
                  Вызов #{{ row.help_call_id }}
                </div>

                <div class="us-row-card">
                  <div class="us-row-card__head">
                    <div class="us-row-card__title-wrap">
                      <q-checkbox
                        v-if="row.can_sign"
                        :model-value="this.docSelectedIds.includes(row.floor_map_document_id)"
                        @update:model-value="(v) => this.toggleDocSelectItem(row.floor_map_document_id, v)"
                      />
                      <h3 class="us-row-card__title">{{ this.prettyDocTypeLabel(row.doc_type, 'Документ') }} #{{ row.floor_map_document_id }}</h3>
                      <span v-if="this.showServiceFields" class="us-id-chip">ID: {{ row.floor_map_document_id }}</span>
                    </div>
                    <span class="us-row-card__date">{{ row.doc_create_date || "Без даты" }}</span>
                  </div>

                  <div class="us-row-grid">
                    <div class="us-grid-col">
                      <div class="us-grid-col__label">Проект</div>
                      <a :href="buildProjectLink(row, 'M__ProjectFormDocumentTab')" target="_blank" rel="noopener" class="proj-link">
                        {{ row.sbs_project_name || row.project_name }}
                      </a>
                      <div class="us-meta-tags">
                        <span v-if="row.project_type_name" class="us-meta-tag">{{ row.project_type_name }}</span>
                        <span v-if="row.block_name" class="us-meta-tag">Блок {{ row.block_name }}</span>
                        <span v-if="row.floors" class="us-meta-tag">Этажи {{ row.floors }}</span>
                      </div>
                      <div v-if="this.docWorksText(row.works)" class="us-project-works">
                        <span class="us-project-works__label">Работы:</span>
                        <span class="us-project-works__value">{{ this.docWorksText(row.works) }}</span>
                      </div>
                    </div>

                    <div class="us-grid-col">
                      <div class="us-grid-col__label">Контрагент</div>
                      <div class="doc-cell-contractor">
                        <span v-if="row.contractor_name" class="doc-contractor-name">{{ row.contractor_name }}</span>
                        <span v-else class="doc-contractor-empty">—</span>
                      </div>
                    </div>

                    <div class="us-grid-col">
                      <div class="us-grid-col__label">Документы</div>
                      <article :class="['us-doc-mini-card', row.can_sign ? 'us-doc-mini-card--blue' : row.all_signed ? 'us-doc-mini-card--green' : 'us-doc-mini-card--beige']">
                        <div class="us-doc-mini-card__head">
                          <div>
                            <h4 class="us-doc-mini-card__title">{{ this.prettyDocTypeLabel(row.doc_type, "Документ") }}</h4>
                            <p class="us-doc-mini-card__date">{{ row.doc_create_date || "Дата не указана" }}</p>
                          </div>
                          <div class="us-doc-mini-card__badges">
                            <span v-if="row.all_signed" class="us-badge us-badge--signed">Подписано</span>
                            <span v-else-if="row.can_sign" class="us-badge us-badge--muted">Требует подписи</span>
                            <span v-else class="us-badge us-badge--muted">Не подписан</span>
                            <span
                              v-if="showEsfBadgeInDocJournal(row)"
                              :class="['us-badge', hasEsfInfo(row) ? 'us-badge--esf' : 'us-badge--esf-no']"
                              :title="esfTooltipText(row)"
                            >{{ esfBadgeLabel(row) }}</span>
                            <q-btn
                              flat
                              dense
                              round
                              icon="picture_as_pdf"
                              color="primary"
                              size="sm"
                              :loading="!!this.docDownloading[row.floor_map_document_id]"
                              @click.stop="this.onDownloadDocument(row)"
                            >
                              <q-tooltip>Открыть PDF</q-tooltip>
                            </q-btn>
                          </div>
                        </div>

                        <div
                          v-if="row.avr_sum || row.is_avr_sent_bi || this.showServiceFields"
                          class="us-info-block"
                        >
                          <div v-if="row.avr_sum" class="us-info-line">
                            Сумма: {{ Number(row.avr_sum).toLocaleString("ru-RU") }} ₸
                          </div>
                          <div v-if="row.is_avr_sent_bi" class="us-info-line">Отправлено в 1С</div>
                          <div v-if="this.showServiceFields" class="us-info-line">[floor_map_document_id]: {{ row.floor_map_document_id }}</div>
                          <div v-if="this.showServiceFields" class="us-info-line">[project_entrance_id]: {{ row.project_entrance_id ?? '\u2014' }}</div>
                          <div v-if="this.showServiceFields" class="us-info-line">[project_id]: {{ row.project_id ?? '\u2014' }}</div>
                          <div v-if="this.showServiceFields" class="us-info-line">[sbs_project_id]: {{ row.sbs_project_id ?? '\u2014' }}</div>
                        </div>

                        <div v-if="(row.signers || []).length" class="doc-signers doc-signers--in-card">
                          <div
                            v-for="(signer, signerIndex) in (row.signers || [])"
                            :key="`${row.floor_map_document_id}_${signer.assign_type_code || 'role'}_${signer.person_fio || signer.person_name || signerIndex}_${signer.sign_date || ''}`"
                            :class="['doc-signer', signer.is_signed ? 'doc-signer--done' : 'doc-signer--waiting', signer.is_me ? 'doc-signer--me' : '']"
                          >
                            <q-icon :name="signer.is_signed ? 'check_circle' : 'schedule'" :class="['doc-signer__icon', signer.is_signed ? 'doc-signer__icon--done' : 'doc-signer__icon--wait']" size="15px" />
                            <div class="doc-signer__info">
                              <span class="doc-signer__role">{{ signer.sign_role }}</span>
                              <span :class="['doc-signer__fio', signer.is_signed ? 'doc-signer__fio--done' : '']">{{ signer.person_fio }}</span>
                              <span v-if="signer.is_signed && signer.sign_date" class="doc-signer__date">
                                <q-icon name="event_available" size="10px" /> {{ signer.sign_date }}
                              </span>
                            </div>
                            <span v-if="signer.is_me" class="doc-signer__me-badge">Я</span>
                          </div>
                        </div>

                        <div v-if="row.can_sign" class="us-doc-mini-card__actions">
                          <q-btn
                            v-if="row.can_sign"
                            unelevated
                            no-caps
                            class="us-sign-btn"
                            :loading="!!this.docSigning[row.floor_map_document_id]"
                            @click.stop="this.onSignDocument(row)"
                            label="Подписать"
                          />
                        </div>

                        <div v-if="showK2svProrabCheckedLine(row)" class="doc-prorab-k2sv-line">
                          <q-icon name="verified" size="12px" class="q-mr-xs" color="positive" />
                          Согласовано прорабом<span v-if="row.prorab_k2sv_checked_fio">: {{ row.prorab_k2sv_checked_fio }}</span><span v-if="row.prorab_k2sv_checked_at">, {{ row.prorab_k2sv_checked_at }}</span>
                        </div>
                      </article>
                    </div>
                  </div>
                </div>
              </template>
            </template>
          </div>
          </div>

          <div v-if="this.doc.tabs[docTabName].pages > 1" class="us-pagination">
            <q-pagination
              :model-value="this.doc.tabs[docTabName].page"
              :max="this.doc.tabs[docTabName].pages"
              max-pages="7"
              dense
              outline
              direction-links
              color="primary"
              size="sm"
              @update:model-value="this.onDocPageChange"
            />
          </div>
        </div>
      </q-tab-panel>
      </template>

    </q-tab-panels>
    </div>

    <!-- ===== МОДАЛКА: СПИСОК МАТЕРИАЛОВ ===== -->
    <q-dialog v-model="this.invMaterials.show">
      <q-card style="min-width:500px; max-width:90vw; width:auto;">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-subtitle1 text-weight-medium">
            Материалы по накладной №&nbsp;{{ this.invMaterials.invoice_num }}
          </div>
          <q-space />
          <q-btn icon="close" flat round dense @click="this.invMaterials.show = false" />
        </q-card-section>
        <q-card-section>
          <q-list separator dense>
              <q-item v-for="(mat, i) in this.invMaterials.items" :key="i">
                <q-item-section avatar>
                  <q-avatar size="24px" color="primary" text-color="white" font-size="11px">{{ i + 1 }}</q-avatar>
                </q-item-section>
                <q-item-section style="white-space:normal; word-break:break-word;">{{ mat }}</q-item-section>
              </q-item>
            <q-item v-if="!this.invMaterials.items.length">
              <q-item-section class="text-grey-5">Нет данных</q-item-section>
            </q-item>
          </q-list>
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- ===== МОДАЛКА: ВИДЫ РАБОТ ===== -->
    <q-dialog v-model="this.docWorks.show">
      <q-card style="min-width:420px; max-width:90vw; width:auto;">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-subtitle1 text-weight-medium">
            Виды работ — {{ this.docWorks.doc_type }}
          </div>
          <q-space />
          <q-btn icon="close" flat round dense @click="this.docWorks.show = false" />
        </q-card-section>
        <q-card-section>
          <q-list separator dense>
            <q-item v-for="(work, i) in this.docWorks.items" :key="i">
              <q-item-section avatar>
                <q-avatar size="24px" color="teal" text-color="white" font-size="11px">{{ i + 1 }}</q-avatar>
              </q-item-section>
              <q-item-section style="white-space:normal; word-break:break-word;">{{ work }}</q-item-section>
            </q-item>
            <q-item v-if="!this.docWorks.items.length">
              <q-item-section class="text-grey-5">Нет данных</q-item-section>
            </q-item>
          </q-list>
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- ===== ДИАЛОГ ВЫБОРА ПРОЕКТА УСП (накладные / договора) ===== -->
    <q-dialog v-model="this.uspDialog.show" persistent>
      <q-card style="min-width: 800px; max-width: 1000px;">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">Выбор проекта УСП</div>
          <q-space />
          <q-btn icon="close" flat round dense @click="this.uspDialog.show = false" />
        </q-card-section>

        <q-card-section>
          <q-input
            v-model="this.uspDialog.search"
            outlined dense clearable autofocus
            placeholder="Поиск по названию проекта или типу...">
            <template #prepend>
              <q-icon name="search" />
            </template>
          </q-input>

          <div style="margin-top: 16px;">
            <q-linear-progress v-if="this.uspDialog.loading" indeterminate />

            <div v-else-if="this.uspFilteredRows.length === 0"
              style="text-align:center;padding:40px;color:#999;">
              <q-icon name="folder_off" size="48px" />
              <div style="margin-top:12px;">Проекты не найдены</div>
            </div>

            <q-table
              v-else
              :rows="this.uspFilteredRows"
              :columns="this.uspColumns"
              row-key="project_group_id"
              flat bordered
              :rows-per-page-options="[10, 20, 50]"
              :pagination="{ rowsPerPage: 10 }">
              <template #body-cell-project_guid="props">
                <q-td :props="props">
                  <div style="font-family:monospace;font-size:11px;color:#666;">
                    {{ props.value }}
                  </div>
                </q-td>
              </template>
              <template #body-cell-actions="props">
                <q-td :props="props">
                  <q-btn label="Выбрать" color="primary" size="sm" unelevated
                    @click="this.onUspProjectSelect(props.row)" />
                </q-td>
              </template>
            </q-table>
          </div>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Отмена" color="grey" @click="this.uspDialog.show = false" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import CustomSelect from "@/components/form/CustomSelect";
import { fetchMenuStatuses } from "@/components/layout/Navbar/services";
import { isChrome, showSuccessToast } from "@/services";
import { useAppStore } from "@/services/store/app";
import { unsignedSignAPI } from "./services/api";
import {
  getResidents,
  getProjectTypes,
  getClosingDocumentEntrances,
} from "@/views/ClosingDocuments/services";
import {
  PAGE_SIZE,
  SIGNED_OPTIONS,
  SEND_TO_1C_OPTIONS,
  MANUAL_OPTIONS,
  invoicesColumns,
  agreementsColumns,
  documentsColumns,
  normalColNames,
  signStatusColor,
  signStatusLabel,
  BUH_DOC_TYPE_IDS,
  EXEC_DOC_TYPE_IDS,
  FLOOR_MAP_DOC_TYPE_ID_BUH_AVR,
  FLOOR_MAP_DOC_TYPE_ID_M19_OVER,
  sortFloorMapDocTypeIdsForTab,
  UNSIGNED_DOC_TYPE_FALLBACK_LABEL,
} from "./services/index";
import {
  DEFAULT_IS_SIGNED,
  DEFAULT_DOC_AUDIT_TAGS,
  docAuditTagsToApiQuery,
  normalizeDocAuditTags,
  parseTabFromQuery,
  parseInvFromQuery,
  parseAgrFromQuery,
  parseDocFromQuery,
  buildReturnUrl as buildReturnUrlFromState,
} from "./services/url-state";

const UNSIGNED_SIGN_TAB_NAMES = ["buh_docs", "exec_docs", "agreements", "invoices"];

/** Значение q-select для режима «все типы» вкладки (не clashes с числовыми id из БД). */
const DOC_TYPE_SELECT_ALL = "__ALL_TYPES__";

/** Фиксированный набор карточек в блоке «Документы» для БУХ-журнала (как в legacy-макете). */
const BUH_DOC_CARD_SLOTS = Object.freeze([
  { key: "ks2v",   typeId: 2, label: "КС-2В",               toneClass: "us-doc-mini-card--blue"  },
  { key: "buhavr", typeId: 7, label: "АВР",                 toneClass: "us-doc-mini-card--beige" },
  { key: "zks",    typeId: 6, label: "3КС",                 toneClass: "us-doc-mini-card--green" },
]);

/** Сначала блок ЭСФ, затем подписание — совпадает с шапкой меню */
const DOC_AUDIT_STATUS_OPTIONS = Object.freeze([
  { value: "esf_yes", label: "\u042d\u0421\u0424 \u0435\u0441\u0442\u044c" },
  { value: "esf_no", label: "\u041d\u0435\u0442 \u042d\u0421\u0424" },
  { value: "sign_signed", label: "\u041f\u043e\u0434\u043f\u0438\u0441\u0430\u043d\u043e" },
  { value: "sign_unsigned", label: "\u041d\u0435 \u043f\u043e\u0434\u043f\u0438\u0441\u0430\u043d\u043e" },
]);
function parseFloorMapDocumentIdFromQuery(query) {
  const raw = query?.floor_map_document_id;
  const s = Array.isArray(raw) ? raw[0] : raw;
  if (s == null || s === "") return null;
  const n = parseInt(String(s), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Заглушка из `floor_map_document__read` при `esf_state = 1` и пустом `esf_status`. */
const ESF_PLACEHOLDER_RU = "\u041f\u0440\u0438\u043d\u044f\u0442 \u043e\u0442 \u043f\u043e\u0441\u0442\u0430\u0432\u0449\u0438\u043a\u0430";

function esfStatusText(row) {
  const s = row?.esf_status;
  if (s == null || typeof s !== "string") return s ? String(s).trim() : "";
  return s.trim();
}

function hasEsfInfo(row) {
  const t = esfStatusText(row);
  if (t === ESF_PLACEHOLDER_RU) return true;
  if (t.length > 0) return true;
  if (Number(row?.esf_state) === 1) return true;
  return false;
}

function esfTooltipText(row) {
  if (!hasEsfInfo(row)) return "\u041d\u0435\u0442 \u0434\u0430\u043d\u043d\u044b\u0445 \u043f\u043e \u042d\u0421\u0424";
  const t = esfStatusText(row);
  if (t === ESF_PLACEHOLDER_RU) return "\u042d\u0421\u0424: \u043f\u0440\u0438\u043d\u044f\u0442 \u043e\u0442 \u043f\u043e\u0441\u0442\u0430\u0432\u0449\u0438\u043a\u0430";
  if (t) return `\u042d\u0421\u0424: ${t}`;
  if (Number(row?.esf_state) === 1) return "\u042d\u0421\u0424: \u043f\u0440\u0438\u043d\u044f\u0442 \u043e\u0442 \u043f\u043e\u0441\u0442\u0430\u0432\u0449\u0438\u043a\u0430";
  return "\u042d\u0421\u0424";
}

function showK2svProrabCheckedLine(row) {
  if (!row?.prorab_k2sv_checked_fio && !row?.prorab_k2sv_checked_at) return false;
  if (row.floor_map_document_type_code && row.floor_map_document_type_code !== "K2SV") return false;
  return true;
}

/** Определяем АВР-строку (BUH_AVR) для бейджа ЭСФ и фильтра в БУХ-журнале. */
function isBuhAvrDocRow(row) {
  const typeId = Number(row?.floor_map_document_type_id);
  if (typeId === 7) return true;
  const code = String(row?.floor_map_document_type_code || "").toUpperCase();
  return code === "BUH_AVR";
}

/** Метки «Есть ЭСФ» / «Нет ЭСФ» показываем только у АВР. */
function showEsfBadgeInDocJournal(row) {
  return isBuhAvrDocRow(row);
}

function esfBadgeLabel(row) {
  return hasEsfInfo(row)
    ? "\u042d\u0421\u0424 \u0435\u0441\u0442\u044c"
    : "\u041d\u0435\u0442 \u042d\u0421\u0424";
}

function normalizeUnsignedDocTypeLabel(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const compact = raw.toUpperCase().replace(/\s+/g, " ");
  if (
    compact.includes("БУХГАЛТЕРСКИЙ АВР 3-2")
    || compact.includes("БУХ АВР 3-2")
    || compact === "АВР 3-2"
  ) {
    return "АВР";
  }
  if (
    compact === "M19_OVER"
    || compact === "M19 ПЕРЕРАСХОД"
    || compact === "M19_ПЕРЕРАСХОД"
    || compact === "M19 OVER"
  ) {
    return "M19";
  }
  return raw;
}

export default {
  name: "UnsignedSign",
  components: { CustomSelect },

  data() {
    return {
      signedOptions: SIGNED_OPTIONS,
      docAuditStatusOptions: DOC_AUDIT_STATUS_OPTIONS,
      sendTo1cOptions: SEND_TO_1C_OPTIONS,
      manualOptions: MANUAL_OPTIONS,
      activeTab:     "buh_docs",

      invoicesColumns,
      agreementsColumns,
      documentsColumns,

      showServiceFields: false,

      // ---- вкладка 1: накладные ----
      inv: {
        data:         [],
        total:        null,
        page:         1,
        pages:        1,
        loading:      false,
        filters: {
          is_signed:        DEFAULT_IS_SIGNED,
          sbs_project_id:   null,
          project_type_id:  null,
          provider_id:      null,
          is_send_to_1c:    null,
          is_manual_signed: null,
          date_from:        null,
          date_to:          null,
        },
        projectLabel:  null,
      },

      // модалка списка материалов
      invMaterials: {
        show:        false,
        invoice_num: null,
        items:       [],
      },

      // модалка видов работ (документы)
      docWorks: {
        show:     false,
        doc_type: null,
        items:    [],
      },

      // ---- вкладка 2: договора ----
      agr: {
        data:         [],
        total:        null,
        page:         1,
        pages:        1,
        loading:      false,
        filters: {
          is_signed:       DEFAULT_IS_SIGNED,
          sbs_project_id:  null,
          project_type_id: null,
          contractor_id:   null,
        },
        projectLabel:  null,
      },

      // ---- вкладки 1-2: документы (общие фильтры + per-tab данные) ----
      doc: {
        // данные хранятся отдельно на каждую вкладку
        tabs: {
          buh_docs:  { data: [], total: null, page: 1, pages: 1, loading: false, typeIds: BUH_DOC_TYPE_IDS.slice() },
          exec_docs: { data: [], total: null, page: 1, pages: 1, loading: false, typeIds: EXEC_DOC_TYPE_IDS.slice() },
        },
        // общие фильтры (ЖК, тип, подъезд, этаж, даты, контрагент)
        filters: {
          doc_audit_tags:       [...DEFAULT_DOC_AUDIT_TAGS],
          resident_id:          null,
          project_type_id:      null,
          project_entrance_id:  null,
          level_num:            null,
          doc_date_from:        null,
          doc_date_to:          null,
          contractor_id:        null,
        },
      },

      // каскадные справочники фильтров документов (как в закрывающих)
      docResidentOptions:       [],
      docFilteredResidentOpts:  [],
      docProjectTypeOptions:    [],
      docContractorOptions:     [],
      docFilteredContractorOpts: [],
      docEntranceOptions:       [],
      docLevelOptions:          [],
      docFiltersLoading:        false,
      docEntrancesLoading:      false,

      // per-row loading state
      invDownloading: {},
      invSigning:     {},
      invSending1C:   {},
      agrDownloading: {},
      agrSigning:     {},
      docDownloading: {},
      docSigning:     {},
      docStornoing:   {},
      docDeleting:    {},

      // batch signing state (documents tab)
      docSelectedIds:  [],
      docBatchSigning: false,

      // справочники
      providersOptions:      [],
      projectTypesOptions:   [],
      contractorsOptions:    [],
      agrContractorsOptions: [],
      entrancesOptions:      [],
      docTypesOptions:       [],

      // диалог выбора проекта УСП
      uspDialog: {
        show:      false,
        search:    "",
        loading:   false,
        targetTab: null,  // 'inv' | 'agr' | 'doc'
      },
      uspAllProjects: [],
      uspColumns: [
        { name: "project_name", label: "Наименование проекта", field: "project_name", align: "left",   sortable: true },
        { name: "project_type", label: "Тип проекта",          field: "project_type", align: "left",   sortable: true },
        { name: "project_guid", label: "GUID",             field: "project_guid", align: "left",   sortable: false },
        { name: "actions",      label: "Действия",         field: "actions",      align: "center" },
      ],

      isFetching: false,
    };
  },

  computed: {
    computedTabs() {
      const fmt = (n) => (n !== null ? ` (${n})` : '');
      return [
        { name: "buh_docs",   label: `БУХ ДОКУМЕНТАЦИЯ${fmt(this.doc.tabs.buh_docs.total)}` },
        { name: "exec_docs",  label: `M19${fmt(this.doc.tabs.exec_docs.total)}` },
        { name: "agreements", label: `ДОГОВОРА ПРОЕКТА${fmt(this.agr.total)}` },
        { name: "invoices",   label: `НАКЛАДНЫЕ${fmt(this.inv.total)}` },
      ];
    },

    docCurrent() {
      const tab = ['buh_docs', 'exec_docs'].includes(this.activeTab) ? this.activeTab : 'buh_docs';
      return this.doc.tabs[tab];
    },

    isDocTabActive() {
      return this.activeTab === 'buh_docs' || this.activeTab === 'exec_docs';
    },

    docAuditMultiTags() {
      const [signTag, esfTag] = normalizeDocAuditTags(this.doc?.filters?.doc_audit_tags);
      const selected = [];
      if (signTag !== "sign_all") selected.push(signTag);
      if (esfTag !== "esf_all") selected.push(esfTag);
      return selected;
    },

    docAuditMultiDisplay() {
      const [signTag, esfTag] = normalizeDocAuditTags(this.doc?.filters?.doc_audit_tags);
      const signPhrase =
        signTag === "sign_signed"
          ? "\u041f\u043e\u0434\u043f\u0438\u0441\u0430\u043d\u043e"
          : signTag === "sign_unsigned"
            ? "\u041d\u0435 \u043f\u043e\u0434\u043f\u0438\u0441\u0430\u043d\u043e"
            : null;
      const esfPhrase =
        esfTag === "esf_yes"
          ? "\u042d\u0421\u0424 \u0435\u0441\u0442\u044c"
          : esfTag === "esf_no"
            ? "\u041d\u0435\u0442 \u042d\u0421\u0424"
            : null;
      if (!signPhrase && !esfPhrase) return "\u0412\u0441\u0435";
      if (signPhrase && !esfPhrase) return signPhrase;
      if (!signPhrase && esfPhrase) return esfPhrase;
      return `${esfPhrase} · ${signPhrase}`;
    },

    /** Группировка БУХ-документов по одному вызову с тремя фиксированными карточками (КС-2В / Бух АВР / 3КС). */
    buhDocCallGroups() {
      const rows = this.doc?.tabs?.buh_docs?.data || [];
      if (!Array.isArray(rows) || rows.length === 0) return [];

      const byKey = new Map();

      const normalizeHelpCallId = (raw) => {
        if (raw == null || raw === "") return null;
        const n = Number(raw);
        return Number.isFinite(n) ? n : null;
      };

      const rowDateTs = (row) => {
        const raw = row?.doc_create_date_raw;
        if (raw == null || raw === "") return 0;
        const ts = Date.parse(String(raw).replace(" ", "T"));
        return Number.isFinite(ts) ? ts : 0;
      };

      for (const row of rows) {
        const callId = normalizeHelpCallId(row?.help_call_id);
        const key = callId != null ? `call_${callId}` : `doc_${row.floor_map_document_id}`;

        let group = byKey.get(key);
        if (!group) {
          group = {
            key,
            help_call_id: callId,
            row,
            contractor_name: row?.contractor_name || null,
            slotRows: {},
          };
          byKey.set(key, group);
        } else if (callId != null && group.help_call_id == null) {
          group.help_call_id = callId;
        }

        if ((!group.row?.sbs_project_name && row?.sbs_project_name) || (!group.row?.project_name && row?.project_name)) {
          group.row = row;
        }
        if (!group.contractor_name && row?.contractor_name) {
          group.contractor_name = row.contractor_name;
        }

        const slotKey = this.detectBuhDocSlotKey(row);
        if (!slotKey) continue;

        const prev = group.slotRows[slotKey];
        if (!prev || rowDateTs(row) >= rowDateTs(prev)) {
          group.slotRows[slotKey] = row;
        }
      }

      // Слияние групп с одним help_call_id (на случай разных ключей из-за типа id в ответе API).
      const mergedByCall = new Map();
      for (const group of byKey.values()) {
        const callId = group.help_call_id;
        if (callId == null) {
          mergedByCall.set(group.key, group);
          continue;
        }
        const mkey = `call_${callId}`;
        let merged = mergedByCall.get(mkey);
        if (!merged) {
          merged = { ...group, key: mkey, slotRows: { ...group.slotRows } };
          mergedByCall.set(mkey, merged);
          continue;
        }
        for (const [slotKey, slotRow] of Object.entries(group.slotRows)) {
          const prev = merged.slotRows[slotKey];
          if (!prev || rowDateTs(slotRow) >= rowDateTs(prev)) {
            merged.slotRows[slotKey] = slotRow;
          }
        }
        if ((!merged.row?.sbs_project_name && group.row?.sbs_project_name) || (!merged.row?.project_name && group.row?.project_name)) {
          merged.row = group.row;
        }
        if (!merged.contractor_name && group.contractor_name) {
          merged.contractor_name = group.contractor_name;
        }
      }

      const groups = [...mergedByCall.values()];

      return groups.map((group) => {
        const cards = BUH_DOC_CARD_SLOTS.map((slot) => ({
          key: `${group.key}_${slot.key}`,
          slotKey: slot.key,
          label: slot.label,
          toneClass: slot.toneClass,
          row: group.slotRows[slot.key] || null,
        }));
        const worksSource = cards.map((c) => c.row).find((r) => r?.works);
        const worksText = this.docWorksText(worksSource?.works || group.row?.works);

        return {
          key: group.key,
          help_call_id: group.help_call_id,
          row: group.row,
          contractor_name: group.contractor_name,
          worksText,
          cards,
        };
      });
    },

    /** Варианты «Тип документа» по вкладкам (единый источник для q-select и быстрых кнопок). */
    unsignedDocTabTypeOpts() {
      const apiById = new Map();
      for (const raw of this.docTypesOptions || []) {
        const id = Number(raw?.floor_map_document_type_id);
        if (Number.isFinite(id)) apiById.set(id, raw);
      }
      const mkRows = (orderedIds) =>
        orderedIds.map((id) => {
          const row = apiById.get(id);
          const nm =
            typeof row?.floor_map_document_type_name === "string"
              ? row.floor_map_document_type_name.trim()
              : "";
          const fb = UNSIGNED_DOC_TYPE_FALLBACK_LABEL?.[id];
          const display = normalizeUnsignedDocTypeLabel(nm || fb || `Тип документа ${id}`);
          return {
            ...row,
            floor_map_document_type_id: id,
            floor_map_document_type_name: display,
          };
        });
      return {
        buh_docs: mkRows(BUH_DOC_TYPE_IDS),
        exec_docs: mkRows(EXEC_DOC_TYPE_IDS),
      };
    },

    uspFilteredRows() {
      if (!this.uspDialog.search) return this.uspAllProjects;
      const q = this.uspDialog.search.toLowerCase();
      return this.uspAllProjects.filter(
        (p) =>
          (p.project_name || "").toLowerCase().includes(q) ||
          (p.project_type || "").toLowerCase().includes(q)
      );
    },

    docSelectableIds() {
      return this.docCurrent.data.filter((r) => r.can_sign).map((r) => r.floor_map_document_id);
    },

    docAllSelected() {
      if (this.docSelectableIds.length === 0) return false;
      return this.docSelectableIds.every((id) => this.docSelectedIds.includes(id));
    },

    docIndeterminate() {
      if (this.docSelectableIds.length === 0) return false;
      const cnt = this.docSelectableIds.filter((id) => this.docSelectedIds.includes(id)).length;
      return cnt > 0 && cnt < this.docSelectableIds.length;
    },

    invVisibleCols() {
      return this.showServiceFields
        ? this.invoicesColumns.map((c) => c.name)
        : normalColNames(this.invoicesColumns);
    },
    agrVisibleCols() {
      return this.showServiceFields
        ? this.agreementsColumns.map((c) => c.name)
        : normalColNames(this.agreementsColumns);
    },
    docVisibleCols() {
      return this.showServiceFields
        ? this.documentsColumns.map((c) => c.name)
        : normalColNames(this.documentsColumns);
    },

    /** ID из URL для режима «один документ» на вкладке exec_docs */
    parsedRouteFloorMapDocumentId() {
      return parseFloorMapDocumentIdFromQuery(this.$route.query);
    },
  },

  watch: {
    "$route.query.tab"(newVal) {
      const raw = Array.isArray(newVal) ? newVal[0] : newVal;
      if (!raw || typeof raw !== "string") return;
      if (!UNSIGNED_SIGN_TAB_NAMES.includes(raw)) return;
      if (raw !== this.activeTab) this.activeTab = raw;
      if (raw !== "exec_docs") {
        this.stripUnsignedSignFloorMapDocumentIdFromRoute();
      }
    },
    "$route.query.floor_map_document_id"() {
      if (this.activeTab !== "exec_docs") return;
      this.loadDocuments(1);
    },
  },

  methods: {
    signStatusColor,
    signStatusLabel,
    hasEsfInfo,
    esfTooltipText,
    isBuhAvrDocRow,
    esfBadgeLabel,
    showK2svProrabCheckedLine,
    showEsfBadgeInDocJournal,

    prettyDocTypeLabel(value, fallback = "") {
      return normalizeUnsignedDocTypeLabel(value) || fallback;
    },

    detectBuhDocSlotKey(row) {
      const byTypeId = Number(row?.floor_map_document_type_id);
      if (byTypeId === 2) return "ks2v";
      if (byTypeId === 7) return "buhavr";
      if (byTypeId === 6) return "zks";

      const code = String(row?.floor_map_document_type_code || "").toUpperCase();
      if (code === "K2SV" || code === "KS2V") return "ks2v";
      if (code === "BUH_AVR" || code.includes("AVR")) return "buhavr";
      if (code === "ZKS" || code === "3KS" || code === "EKS") return "zks";

      const t = String(row?.doc_type || "").toUpperCase();
      if (t.includes("КС-2В") || t.includes("КС2В") || t.includes("KS-2V") || t.includes("KS2V")) return "ks2v";
      if (t.includes("БУХ") || t.includes("АВР")) return "buhavr";
      if (t.includes("3КС") || t.includes("ЗКС") || t.includes("ЭКС") || t.includes("3KC") || t.includes("EKS")) return "zks";
      return null;
    },

    docWorksText(works) {
      if (!works) return "";
      const items = String(works)
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
      return items.join(", ");
    },

    // ── URL ↔ state ───────────────────────────────────────────────────────
    /**
     * Применяет фильтры из query к state. Вызывается ОДИН раз при mount.
     * Логика синхронизации в обратную сторону (state → URL) намеренно
     * отсутствует — URL генерируется только при необходимости через buildReturnUrl.
     */
    applyFiltersFromUrl(query) {
      const inv = parseInvFromQuery(query);
      Object.assign(this.inv.filters, inv.filters);
      this.inv.page = inv.page;

      const agr = parseAgrFromQuery(query);
      Object.assign(this.agr.filters, agr.filters);
      this.agr.page = agr.page;

      const docCommon = parseDocFromQuery(query, "buh_docs");
      Object.assign(this.doc.filters, docCommon.filters);
      this.doc.tabs.buh_docs.typeIds  = BUH_DOC_TYPE_IDS.slice();
      this.doc.tabs.buh_docs.page     = docCommon.page;
      const docExec = parseDocFromQuery(query, "exec_docs");
      this.doc.tabs.exec_docs.typeIds = docExec.typeIds;
      this.doc.tabs.exec_docs.page    = docExec.page;
    },

    /**
     * Подтягивает человекочитаемые лейблы (projectLabel, entranceLabel) по
     * sbs/entrance ID из URL. Вызывается фоном после mount; ничего не ломает,
     * если справочники не успели загрузиться.
     */
    async initLabelsFromUrl() {
      const sbsList = [
        ["inv", this.inv.filters.sbs_project_id],
        ["agr", this.agr.filters.sbs_project_id],
      ].filter(([, sbs]) => sbs != null);
      if (sbsList.length > 0) {
        try {
          if (this.uspAllProjects.length === 0) {
            const res = await unsignedSignAPI.getUspProjects();
            this.uspAllProjects = res?.data || res || [];
          }
          const lookup = (sbs) => {
            const row = this.uspAllProjects.find((p) => Number(p.sbs_project_id) === Number(sbs));
            return row ? row.project_name + (row.project_type ? ` (${row.project_type})` : "") : null;
          };
          for (const [key, sbs] of sbsList) {
            this[key].projectLabel = lookup(sbs);
          }
        } catch (_) { /* label is optional */ }
      }
    },

    async initDocFiltersFromUrl() {
      const saved = { ...this.doc.filters };
      if (!saved.resident_id) return;

      this.docFiltersLoading = true;
      try {
        const typesRes = await getProjectTypes(saved.resident_id);
        this.docProjectTypeOptions = (typesRes?.data || []).map((t) => ({
          label: t.project_type_name,
          value: t.project_type_id,
        }));

        if (saved.project_type_id) {
          await this.loadDocEntrances(saved.resident_id, saved.project_type_id);
          if (saved.project_entrance_id != null) {
            const ok = this.docEntranceOptions.some(
              (e) => e.project_entrance_id === saved.project_entrance_id,
            );
            if (!ok) this.doc.filters.project_entrance_id = null;
          }
        }

        const params = { resident_id: saved.resident_id };
        if (this.doc.filters.project_type_id) params.project_type_id = this.doc.filters.project_type_id;
        if (this.doc.filters.contractor_id) params.contractor_id = this.doc.filters.contractor_id;

        const res = await unsignedSignAPI.getDocumentsCascade(params);
        if (res) {
          this.docContractorOptions = (res.contractors || []).map((c) => ({
            label: c.contractor_name,
            value: c.contractor_id,
          }));
          this.docFilteredContractorOpts = this.docContractorOptions;
          this.docLevelOptions = (res.levels || []).map((l) => ({ label: String(l), value: l }));
          if (this.doc.filters.contractor_id != null) {
            const ok = this.docContractorOptions.some((c) => c.value === this.doc.filters.contractor_id);
            if (!ok) this.doc.filters.contractor_id = null;
          }
          if (this.doc.filters.level_num != null) {
            const ok = this.docLevelOptions.some((l) => l.value === this.doc.filters.level_num);
            if (!ok) this.doc.filters.level_num = null;
          }
        }
      } catch (_) { /* optional */ } finally {
        this.docFiltersLoading = false;
      }
    },

    async loadDocResidents() {
      const res = await getResidents();
      const items = res?.data || [];
      this.docResidentOptions = items.map((r) => ({
        label: r.resident_name,
        value: r.resident_id,
      }));
      this.docFilteredResidentOpts = this.docResidentOptions;
    },

    filterDocResidents(val, update) {
      update(() => {
        if (!val) {
          this.docFilteredResidentOpts = this.docResidentOptions;
          return;
        }
        const q = val.toLowerCase();
        this.docFilteredResidentOpts = this.docResidentOptions.filter(
          (o) => String(o.label || "").toLowerCase().includes(q),
        );
      });
    },

    filterDocContractors(val, update) {
      update(() => {
        if (!val) {
          this.docFilteredContractorOpts = this.docContractorOptions;
          return;
        }
        const q = val.toLowerCase();
        this.docFilteredContractorOpts = this.docContractorOptions.filter(
          (o) => String(o.label || "").toLowerCase().includes(q),
        );
      });
    },

    docEntranceKey(item) {
      return `${String(item?.entrance ?? "")}|${String(item?.block_name ?? "").trim()}`;
    },

    normalizeDocEntranceOptions(list) {
      const raw = Array.isArray(list) ? list : [];
      const seenPe = new Set();
      const deduped = [];
      for (const it of raw) {
        const id = it?.project_entrance_id;
        if (id != null) {
          if (seenPe.has(id)) continue;
          seenPe.add(id);
        }
        deduped.push(it);
      }
      const keyCounts = new Map();
      for (const it of deduped) {
        const k = this.docEntranceKey(it);
        keyCounts.set(k, (keyCounts.get(k) || 0) + 1);
      }
      return deduped.map((it) => {
        const base = it.entrance_name || it.block_name || `Подъезд ${it.entrance ?? ""}`;
        const collision = (keyCounts.get(this.docEntranceKey(it)) || 0) > 1;
        let entrance_name = base;
        if (collision) {
          const pn = it.project_name != null ? String(it.project_name).trim() : "";
          const suffix = pn || (it.project_id != null ? `проект ${it.project_id}` : "");
          entrance_name = suffix ? `${base} — ${suffix}` : base;
        }
        return { ...it, entrance_name };
      });
    },

    async loadDocEntrances(residentId, projectTypeId) {
      this.docEntranceOptions = [];
      const rid = residentId ?? this.doc.filters.resident_id;
      const ptid = projectTypeId ?? this.doc.filters.project_type_id;
      if (!rid || !ptid) return;
      this.docEntrancesLoading = true;
      try {
        const list = await getClosingDocumentEntrances({
          resident_id: rid,
          project_type_id: ptid,
        });
        this.docEntranceOptions = this.normalizeDocEntranceOptions(list);
        if (this.doc.filters.project_entrance_id != null) {
          const ok = this.docEntranceOptions.some(
            (e) => e.project_entrance_id === this.doc.filters.project_entrance_id,
          );
          if (!ok) this.doc.filters.project_entrance_id = null;
        }
      } finally {
        this.docEntrancesLoading = false;
      }
    },

    async refreshDocCascade(resetBelow = [], { skipReload = false } = {}) {
      resetBelow.forEach((k) => { this.doc.filters[k] = null; });

      if (!this.doc.filters.resident_id) {
        this.docProjectTypeOptions = [];
        this.docContractorOptions = [];
        this.docFilteredContractorOpts = [];
        this.docEntranceOptions = [];
        this.docLevelOptions = [];
        if (!skipReload) {
          this.reloadDocumentsUnsignAfterStripIfNeeded(false);
        }
        return;
      }

      this.docFiltersLoading = true;
      try {
        const params = { resident_id: this.doc.filters.resident_id };
        if (this.doc.filters.project_type_id) params.project_type_id = this.doc.filters.project_type_id;
        if (this.doc.filters.contractor_id) params.contractor_id = this.doc.filters.contractor_id;

        const res = await unsignedSignAPI.getDocumentsCascade(params);
        if (!res) return;

        this.docContractorOptions = (res.contractors || []).map((c) => ({
          label: c.contractor_name,
          value: c.contractor_id,
        }));
        this.docFilteredContractorOpts = this.docContractorOptions;
        this.docLevelOptions = (res.levels || []).map((l) => ({ label: String(l), value: l }));
      } finally {
        this.docFiltersLoading = false;
      }

      if (!skipReload) this.reloadDocumentsUnsignAfterStripIfNeeded(false);
    },

    async onDocResidentChange(val) {
      const hadUrlDocId = parseFloorMapDocumentIdFromQuery(this.$route.query) != null;
      this.stripUnsignedSignFloorMapDocumentIdFromRoute();
      this.doc.filters.resident_id = val ?? null;

      this.docProjectTypeOptions = [];
      if (val) {
        this.docFiltersLoading = true;
        try {
          const typesRes = await getProjectTypes(val);
          this.docProjectTypeOptions = (typesRes?.data || []).map((t) => ({
            label: t.project_type_name,
            value: t.project_type_id,
          }));
        } catch { /* silent */ } finally {
          this.docFiltersLoading = false;
        }
      }

      await this.refreshDocCascade([
        "project_type_id",
        "contractor_id",
        "project_entrance_id",
        "level_num",
      ], { skipReload: true });

      if (this.doc.filters.resident_id && this.doc.filters.project_type_id) {
        await this.loadDocEntrances();
      }

      this.reloadDocumentsUnsignAfterStripIfNeeded(hadUrlDocId);
    },

    async onDocProjectTypeChange(val) {
      const hadUrlDocId = parseFloorMapDocumentIdFromQuery(this.$route.query) != null;
      this.stripUnsignedSignFloorMapDocumentIdFromRoute();
      this.doc.filters.project_type_id = val ?? null;
      await this.refreshDocCascade(["contractor_id", "project_entrance_id", "level_num"], { skipReload: true });
      await this.loadDocEntrances();
      this.reloadDocumentsUnsignAfterStripIfNeeded(hadUrlDocId);
    },

    async onDocContractorChange(val) {
      const hadUrlDocId = parseFloorMapDocumentIdFromQuery(this.$route.query) != null;
      this.stripUnsignedSignFloorMapDocumentIdFromRoute();
      this.doc.filters.contractor_id = val ?? null;
      await this.refreshDocCascade(["level_num"], { skipReload: true });
      this.reloadDocumentsUnsignAfterStripIfNeeded(hadUrlDocId);
    },

    async onDocProjectEntranceChange(val) {
      const hadUrlDocId = parseFloorMapDocumentIdFromQuery(this.$route.query) != null;
      this.stripUnsignedSignFloorMapDocumentIdFromRoute();
      this.doc.filters.project_entrance_id = val ?? null;
      await this.refreshDocCascade(["level_num"], { skipReload: true });
      this.reloadDocumentsUnsignAfterStripIfNeeded(hadUrlDocId);
    },

    onDocResidentInputValueUpdate(val) {
      const empty = val === "" || val == null;
      if (!empty) return;
      if (this.doc.filters.resident_id == null) return;
      this.onDocResidentChange(null);
    },

    /**
     * Возвращает абсолютный URL текущего состояния активного таба.
     * Используется для передачи в API подписания как `return_url` —
     * чтобы пользователя вернули к тем же фильтрам после подписания.
     *
     * При необходимости включить single-doc режим (floor_map_document_id для exec_docs)
     * передайте { floor_map_document_id: <id> } в extras.
     */
    buildReturnUrl(extras = {}) {
      const tab = this.activeTab;
      let tabState;
      if (tab === "invoices") {
        tabState = { filters: this.inv.filters, page: this.inv.page };
      } else if (tab === "agreements") {
        tabState = { filters: this.agr.filters, page: this.agr.page };
      } else {
        tabState = {
          filters: this.doc.filters,
          typeIds: this.doc.tabs[tab].typeIds,
          page:    this.doc.tabs[tab].page,
        };
      }
      // floor_map_document_id оставляем только для exec_docs
      const finalExtras = { ...extras };
      if (tab !== "exec_docs") delete finalExtras.floor_map_document_id;
      return buildReturnUrlFromState("/unsigned-sign", tab, finalExtras, tabState);
    },

    buildProjectLink(row, tab) {
      const params = new URLSearchParams();
      if (row.resident_id)         params.set('resident_id', row.resident_id);
      if (row.project_type_id)     params.set('project_type_id', row.project_type_id);
      if (row.project_entrance_id) params.set('project_entrance_id', row.project_entrance_id);
      if (row.project_id)          params.set('project_id', row.project_id);
      if (row.sbs_project_id)      params.set('sbs_project_id', row.sbs_project_id);
      params.set('tab', tab);
      return `/residential-settings?${params.toString()}`;
    },

    invoiceCounterpartyNames(row) {
      const uniq = new Set();
      const pushIf = (value) => {
        const text = String(value || "").trim();
        if (text) uniq.add(text);
      };

      pushIf(row?.contractor_name);
      pushIf(row?.provider_name);

      for (const party of row?.parties || []) {
        pushIf(party?.person_name);
        pushIf(party?.company_name);
      }

      return [...uniq];
    },

    invSignStatusText(row) {
      const contractorSigned = row.contractor_sign_status === 'SIGNED';
      const providerSigned   = row.provider_sign_status   === 'SIGNED';
      if (row.contractor_sign_status === 'FAILED' || row.provider_sign_status === 'FAILED') return 'Ошибка подписания';
      if (contractorSigned && providerSigned) return 'Подписано обеими сторонами';
      if (contractorSigned) return 'Подписано нач. участка';
      if (providerSigned)   return 'Подписано поставщиком';
      return 'Ожидает подписи нач. участка и поставщика';
    },

    stripUnsignedSignFloorMapDocumentIdFromRoute() {
      if (this.$route.query.floor_map_document_id == null || this.$route.query.floor_map_document_id === "") return false;
      const q = { ...this.$route.query };
      delete q.floor_map_document_id;
      this.$router.replace({ query: q }).catch(() => {});
      return true;
    },

    /** Снимает `floor_map_document_id` из URL и перезагружает документы после применения фильтров (избегает гонки с $route). */
    reloadDocumentsUnsignAfterStripIfNeeded(hadUrlDocId) {
      if (hadUrlDocId) this.$nextTick(() => this.loadDocuments(1));
      else this.loadDocuments(1);
    },

    // ── Табы ──────────────────────────────────────────────────────────────
    onTabChange(tab) {
      const docTabs = ['buh_docs', 'exec_docs'];
      if (docTabs.includes(tab)) {
        this.$nextTick(() => this.loadDocuments());
      }
      this.activeTab = tab;
      // При переключении таба чистим URL целиком — оставляем только ?tab=<name>.
      // Фильтры из URL не переносим, чтобы не тащить состояние одного таба в другой.
      const qTab = this.$route.query.tab;
      const cur = Array.isArray(qTab) ? qTab[0] : qTab;
      const otherKeys = Object.keys(this.$route.query).filter((k) => k !== "tab");
      if (String(cur ?? "") !== String(tab) || otherKeys.length > 0) {
        this.$router.push({ query: { tab } }).catch(() => {});
      }
    },

    async loadCurrentTab() {
      if      (this.activeTab === "invoices")   await this.loadInvoices();
      else if (this.activeTab === "agreements") await this.loadAgreements();
      else if (this.activeTab === "buh_docs" || this.activeTab === "exec_docs") await this.loadDocuments();
    },

    // ── Диалог "Проект УСП" ──
    async openUspDialog(targetTab) {
      this.uspDialog.targetTab = targetTab;
      this.uspDialog.search    = "";
      this.uspDialog.show      = true;

      if (this.uspAllProjects.length === 0) {
        this.uspDialog.loading = true;
        try {
          const res = await unsignedSignAPI.getUspProjects();
          this.uspAllProjects = res?.data || res || [];
        } catch (e) {
          this.$q.notify({ type: "negative", message: "Ошибка загрузки проектов УСП" });
        } finally {
          this.uspDialog.loading = false;
        }
      }
    },

    onUspProjectSelect(row) {
      const label = row.project_name + (row.project_type ? ` (${row.project_type})` : "");
      const sbsProjectId = row.sbs_project_id;
      const tab   = this.uspDialog.targetTab;

      if (tab === "inv") {
        this.inv.filters.sbs_project_id = sbsProjectId ?? null;
        this.inv.projectLabel            = label;
        this.uspDialog.show = false;
        this.loadInvoices(1);
      } else if (tab === "agr") {
        this.agr.filters.sbs_project_id = sbsProjectId ?? null;
        this.agr.projectLabel            = label;
        this.uspDialog.show = false;
        this.loadAgreements(1);
      }
    },

    // ── Вкладка 1: накладные ─────────────────────────────────────────────
    onInvFilter(key, value) {
      this.inv.filters[key] = value ?? null;
      this.loadInvoices(1);
    },
    clearInvProject() {
      this.inv.filters.sbs_project_id = null;
      this.inv.projectLabel = null;
      this.loadInvoices(1);
    },
    openInvMaterialsModal(row) {
      this.invMaterials.invoice_num = row.invoice_num || row.provider_request_invoice_id;
      this.invMaterials.items = row.materials
        ? row.materials.split('\n').filter(Boolean)
        : [];
      this.invMaterials.show = true;
    },

    openDocWorksModal(row) {
      this.docWorks.doc_type = this.prettyDocTypeLabel(row.doc_type, `Документ #${row.floor_map_document_id}`);
      this.docWorks.items    = row.works ? row.works.split(', ').filter(Boolean) : [];
      this.docWorks.show     = true;
    },
    resetInvFilters() {
      this.inv.page = 1;
      this.inv.filters = {
        is_signed:        DEFAULT_IS_SIGNED,
        sbs_project_id:   null,
        project_type_id:  null,
        provider_id:      null,
        is_send_to_1c:    null,
        is_manual_signed: null,
        date_from:        null,
        date_to:          null,
      };
      this.inv.projectLabel = null;
      this.$router.replace({ query: { tab: "invoices" } }).catch(() => {});
      this.loadInvoices(1);
    },
    onInvPageChange(page) {
      this.inv.page = page;
      this.loadInvoices(page);
    },

    async loadInvoices(page) {
      if (page !== undefined) this.inv.page = page;
      this.inv.loading = true;
      this.isFetching  = true;
      try {
        const res = await unsignedSignAPI.getInvoices({
          ...this.inv.filters,
          page:  this.inv.page,
          limit: PAGE_SIZE,
        });
        this.inv.data  = res?.data        || [];
        this.inv.total = parseInt(res?.total_count) || 0;
        this.inv.pages = Math.ceil(this.inv.total / PAGE_SIZE) || 1;
      } catch (e) {
        this.$q.notify({ type: "negative", message: e?.message || "Ошибка загрузки накладных" });
      } finally {
        this.inv.loading = false;
        this.isFetching  = false;
      }
    },

    agrTypeLabel(code) {
      const map = {
        SBS_BIG_CONTRACTOR: 'Гл. подрядчик',
        SBS_BIG_SR:         'СМР',
        SBS_SR_CONTRACTOR:  'Подрядчик СМР',
        SBS_DS:             'Доп. соглашение',
      };
      return map[code] || code;
    },

    // ── Вкладка 2: договора ──────────────────────────────────────────────
    onAgrFilter(key, value) {
      this.agr.filters[key] = value ?? null;
      this.loadAgreements(1);
    },
    clearAgrProject() {
      this.agr.filters.sbs_project_id = null;
      this.agr.projectLabel = null;
      this.loadAgreements(1);
    },
    resetAgrFilters() {
      this.agr.page = 1;
      this.agr.filters = {
        is_signed:       DEFAULT_IS_SIGNED,
        sbs_project_id:  null,
        project_type_id: null,
        contractor_id:   null,
      };
      this.agr.projectLabel = null;
      this.$router.replace({ query: { tab: "agreements" } }).catch(() => {});
      this.loadAgreements(1);
    },
    onAgrPageChange(page) {
      this.agr.page = page;
      this.loadAgreements(page);
    },

    async loadAgreements(page) {
      if (page !== undefined) this.agr.page = page;
      this.agr.data    = [];
      this.agr.loading = true;
      this.isFetching  = true;
      try {
        const res = await unsignedSignAPI.getAgreements({
          ...this.agr.filters,
          page:  this.agr.page,
          limit: PAGE_SIZE,
        });
        this.agr.data  = res?.data        || [];
        this.agr.total = parseInt(res?.total_count) || 0;
        this.agr.pages = Math.ceil(this.agr.total / PAGE_SIZE) || 1;
      } catch (e) {
        this.$q.notify({ type: "negative", message: e?.message || "Ошибка загрузки договоров" });
      } finally {
        this.agr.loading = false;
        this.isFetching  = false;
      }
    },

    // ── Вкладка 3: документы ─────────────────────────────────────────────
    docTabSingleTypeOptions(tabName) {
      const o = this.unsignedDocTabTypeOpts;
      const rows = tabName === "buh_docs" ? o.buh_docs : o.exec_docs;
      return [
        {
          floor_map_document_type_id: DOC_TYPE_SELECT_ALL,
          floor_map_document_type_name: "Все типы",
        },
        ...rows,
      ];
    },

    docTabSingleTypeModel(tabName) {
      const ids = this.doc.tabs[tabName]?.typeIds || [];
      if (ids.length !== 1) {
        const fullSet = tabName === "buh_docs" ? BUH_DOC_TYPE_IDS : EXEC_DOC_TYPE_IDS;
        const sel = new Set(ids.map(Number));
        if (sel.size === fullSet.length && fullSet.every((id) => sel.has(Number(id)))) {
          return DOC_TYPE_SELECT_ALL;
        }
        return null;
      }
      return Number(ids[0]);
    },

    onDocTabSingleTypeFilter(tabName, value) {
      const fullIds = tabName === "buh_docs" ? BUH_DOC_TYPE_IDS : EXEC_DOC_TYPE_IDS;
      if (value === DOC_TYPE_SELECT_ALL || value === null || value === undefined) {
        this.onDocTabTypesFilter(tabName, [...fullIds]);
      } else {
        this.onDocTabTypesFilter(tabName, [Number(value)]);
      }
    },

    /** Смена набора типов для панели `buh_docs`/`exec_docs` (у каждой — свой список опций). */
    onDocTabTypesFilter(tabName, value) {
      const hadUrlDocId = parseFloorMapDocumentIdFromQuery(this.$route.query) != null;
      this.stripUnsignedSignFloorMapDocumentIdFromRoute();
      this._assignDocTabTypeIds(tabName, value);
      if (tabName === this.activeTab) {
        this.reloadDocumentsUnsignAfterStripIfNeeded(hadUrlDocId);
      }
    },

    _assignDocTabTypeIds(tabName, value) {
      const fallbackIds = tabName === "buh_docs" ? BUH_DOC_TYPE_IDS : EXEC_DOC_TYPE_IDS;
      const list = Array.isArray(value) ? value : [];
      const nums = list.map((id) => Number(id)).filter((n) => Number.isFinite(n));
      const raw = nums.length ? nums : fallbackIds.slice();
      this.doc.tabs[tabName].typeIds = sortFloorMapDocTypeIdsForTab(raw, tabName);
    },

    onDocFilter(key, value) {
      const hadUrlDocId = parseFloorMapDocumentIdFromQuery(this.$route.query) != null;
      this.stripUnsignedSignFloorMapDocumentIdFromRoute();
      if (key === 'floor_map_document_type_ids') {
        const tab = ['buh_docs', 'exec_docs'].includes(this.activeTab) ? this.activeTab : 'buh_docs';
        this._assignDocTabTypeIds(tab, value);
      } else if (key === 'doc_audit_tags') {
        this.doc.filters.doc_audit_tags = [...normalizeDocAuditTags(Array.isArray(value) ? value : [])];
      } else {
        this.doc.filters[key] = value ?? null;
      }
      this.reloadDocumentsUnsignAfterStripIfNeeded(hadUrlDocId);
    },
    onDocAuditMultiChange(values) {
      const selected = Array.isArray(values) ? values : [];
      const signValues = selected.filter((v) => String(v).startsWith("sign_"));
      const esfValues = selected.filter((v) => String(v).startsWith("esf_"));
      const sign = signValues.length ? signValues[signValues.length - 1] : "sign_all";
      const esf = esfValues.length ? esfValues[esfValues.length - 1] : "esf_all";
      this.onDocFilter("doc_audit_tags", [sign, esf]);
      this.$nextTick(() => {
        const rawRef = this.$refs?.docAuditSelect;
        const selectRef = Array.isArray(rawRef) ? rawRef[0] : rawRef;
        if (selectRef && typeof selectRef.showPopup === "function") {
          selectRef.showPopup();
        }
      });
    },

    resetDocFilters() {
      const hadUrlDocId = parseFloorMapDocumentIdFromQuery(this.$route.query) != null;
      this.stripUnsignedSignFloorMapDocumentIdFromRoute();
      this.doc.filters = {
        doc_audit_tags:      [...DEFAULT_DOC_AUDIT_TAGS],
        resident_id:         null,
        project_type_id:     null,
        project_entrance_id: null,
        level_num:           null,
        doc_date_from:       null,
        doc_date_to:         null,
        contractor_id:       null,
      };
      this.doc.tabs.buh_docs.typeIds  = BUH_DOC_TYPE_IDS.slice();
      this.doc.tabs.exec_docs.typeIds = EXEC_DOC_TYPE_IDS.slice();
      this.doc.tabs.buh_docs.page     = 1;
      this.doc.tabs.exec_docs.page    = 1;
      this.docProjectTypeOptions      = [];
      this.docContractorOptions       = [];
      this.docFilteredContractorOpts  = [];
      this.docEntranceOptions         = [];
      this.docLevelOptions            = [];
      this.docSelectedIds             = [];
      this.$router.replace({ query: { tab: this.activeTab } }).catch(() => {});
      this.reloadDocumentsUnsignAfterStripIfNeeded(hadUrlDocId);
    },
    onDocPageChange(page) {
      this.loadDocuments(page);
    },

    async loadDocuments(page, forTab) {
      const targetTab = forTab || (['buh_docs', 'exec_docs'].includes(this.activeTab) ? this.activeTab : 'buh_docs');
      const tabState  = this.doc.tabs[targetTab];
      const urlSingleDocId = targetTab === 'exec_docs' ? this.parsedRouteFloorMapDocumentId : null;
      if (urlSingleDocId) tabState.page = 1;
      else if (page !== undefined) tabState.page = page;

      tabState.loading = true;
      if (!forTab) this.isFetching = true;
      try {
        const { doc_audit_tags, project_entrance_id, ...otherFilters } = this.doc.filters;
        const { is_signed, has_esf } = docAuditTagsToApiQuery(doc_audit_tags);
        const typeIds = tabState.typeIds?.length ? tabState.typeIds
                        : (targetTab === 'buh_docs' ? BUH_DOC_TYPE_IDS : EXEC_DOC_TYPE_IDS);
        const res = await unsignedSignAPI.getDocuments({
          resident_id:                 otherFilters.resident_id ?? undefined,
          project_type_id:             otherFilters.project_type_id ?? undefined,
          level_num:                   otherFilters.level_num ?? undefined,
          contractor_id:               otherFilters.contractor_id ?? undefined,
          doc_date_from:               otherFilters.doc_date_from ?? undefined,
          doc_date_to:                 otherFilters.doc_date_to ?? undefined,
          is_signed,
          has_esf,
          project_entrance_ids:        project_entrance_id ? String(project_entrance_id) : undefined,
          floor_map_document_type_ids: typeIds.join(','),
          page:  tabState.page,
          limit: PAGE_SIZE,
        });
        let rows = res?.data || [];

        if (urlSingleDocId) {
          rows = rows.filter((r) => Number(r.floor_map_document_id) === urlSingleDocId);
        }
        tabState.data  = rows;
        if (urlSingleDocId) {
          tabState.total = rows.length;
          tabState.pages = 1;
          tabState.page = 1;
        } else {
          tabState.total = parseInt(res?.total_count) || 0;
          tabState.pages = Math.ceil(tabState.total / PAGE_SIZE) || 1;
        }
        if (targetTab === this.activeTab) {
          this.docSelectedIds = this.docSelectedIds.filter(
            (id) => tabState.data.some((r) => r.floor_map_document_id === id)
          );
        }
      } catch (e) {
        this.$q.notify({ type: "negative", message: e?.message || "Ошибка загрузки документов" });
      } finally {
        tabState.loading = false;
        if (!forTab) this.isFetching = false;
      }
    },

    // ── Скачивание / Подписание ───────────────────────────────────────────
    _preparePdfPreviewWindow() {
      const previewWindow = window.open("", "_blank");
      if (previewWindow?.document) {
        previewWindow.document.title = "Открытие PDF...";
        previewWindow.document.body.style.fontFamily = "Inter, system-ui, sans-serif";
        previewWindow.document.body.style.padding = "24px";
        previewWindow.document.body.style.color = "#1D2939";
        previewWindow.document.body.textContent = "Подготавливаем PDF...";
      }
      return previewWindow;
    },
    _openBlobInNewTab(response, previewWindow = null) {
      const contentType = response?.headers?.["content-type"] || "application/pdf";
      const blob = new Blob([response?.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);

      if (previewWindow && !previewWindow.closed) {
        previewWindow.location.href = url;
      } else {
        const opened = window.open(url, "_blank", "noopener,noreferrer");
        if (!opened) {
          const link = document.createElement("a");
          link.href = url;
          link.target = "_blank";
          link.rel = "noopener noreferrer";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }

      window.setTimeout(() => window.URL.revokeObjectURL(url), 120000);
    },
    _extractErrorText(value) {
      if (value == null) return "";
      if (typeof value === "string") return value.trim();
      if (Array.isArray(value)) {
        const text = value
          .map((item) => this._extractErrorText(item))
          .filter(Boolean)
          .join("; ");
        return text.trim();
      }
      if (typeof value === "object") {
        const directKeys = ["error", "message", "detail", "msg", "title"];
        for (const key of directKeys) {
          const text = this._extractErrorText(value[key]);
          if (text) return text;
        }
        const joined = Object.entries(value)
          .filter(([k]) => k !== "status")
          .map(([, v]) => this._extractErrorText(v))
          .filter(Boolean)
          .join("; ");
        return joined.trim();
      }
      return String(value).trim();
    },
    async _extractHttpErrorMessage(error, fallbackMessage) {
      const status = error?.response?.status;
      const statusText = error?.response?.statusText;
      const data = error?.response?.data;
      let detail = "";

      try {
        if (data instanceof Blob) {
          const raw = (await data.text())?.trim();
          if (raw) {
            try {
              detail = this._extractErrorText(JSON.parse(raw));
            } catch (_) {
              detail = this._extractErrorText(raw);
            }
          }
        } else if (data instanceof ArrayBuffer) {
          const raw = new TextDecoder("utf-8").decode(data).trim();
          if (raw) {
            try {
              detail = this._extractErrorText(JSON.parse(raw));
            } catch (_) {
              detail = this._extractErrorText(raw);
            }
          }
        } else if (data != null) {
          detail = this._extractErrorText(data);
        }
      } catch (_) {
        // no-op: fallback below
      }

      if (!detail) {
        detail = this._extractErrorText(error?.message) || fallbackMessage;
      }

      const statusLine = status ? `${status}${statusText ? `, ${statusText}` : ""}` : "";
      if (statusLine && detail) return `${statusLine}\n${detail}`;
      if (statusLine) return `${statusLine}\n${fallbackMessage}`;
      return detail || fallbackMessage;
    },

    async onDownloadInvoice(row) {
      const id = row.provider_request_invoice_id;
      const previewWindow = this._preparePdfPreviewWindow();
      this.invDownloading = { ...this.invDownloading, [id]: true };
      try {
        const res = await unsignedSignAPI.downloadInvoice(id);
        this._openBlobInNewTab(res, previewWindow);
      } catch (e) {
        if (previewWindow && !previewWindow.closed) previewWindow.close();
        const message = await this._extractHttpErrorMessage(e, "Ошибка формирования накладной");
        this.$q.notify({ type: "negative", message });
      } finally {
        this.invDownloading = { ...this.invDownloading, [id]: false };
      }
    },

    async onSignInvoice(row) {
      const id = row.provider_request_invoice_id;
      this.invSigning = { ...this.invSigning, [id]: true };
      try {
        const res = await unsignedSignAPI.signInvoiceContractor(id, this.buildReturnUrl());
        if (!res?.sign_url) {
          this.$q.notify({ type: "negative", message: "Сервис подписи не вернул ссылку" });
          return;
        }
        window.location.href = res.sign_url;
      } catch (e) {
        this.$q.notify({ type: "negative", message: e?.response?.data?.error || "Ошибка создания процесса подписания" });
      } finally {
        this.invSigning = { ...this.invSigning, [id]: false };
      }
    },

    getInv1CLabel(row) {
      const isProjectTeam  = row.provider_request_initiator_type_code === 'PROJECT_TEAM';
      const isTradingHouse = row.is_trading_house === true;
      if (isProjectTeam && isTradingHouse)  return 'В 1С ТД + КП';
      if (isProjectTeam && !isTradingHouse) return 'В 1С КП';
      if (!isProjectTeam && isTradingHouse) return 'В 1С ТД';
      return null;
    },

    async onSendInvoiceTo1C(row) {
      const id = row.provider_request_invoice_id;
      this.invSending1C = { ...this.invSending1C, [id]: true };
      try {
        const isProjectTeam  = row.provider_request_initiator_type_code === 'PROJECT_TEAM';
        const isTradingHouse = row.is_trading_house === true;
        let targetSystem;
        if (isProjectTeam && isTradingHouse)       targetSystem = 'td_kp';
        else if (isProjectTeam && !isTradingHouse)  targetSystem = 'kp';
        else if (!isProjectTeam && isTradingHouse)  targetSystem = 'td';
        else return;
        await unsignedSignAPI.sendInvoiceTo1C(id, targetSystem);
        this.$q.notify({ type: 'positive', message: 'Успешно отправлено в 1С' });
        await this.loadInvoices();
      } catch (e) {
        this.$q.notify({ type: 'negative', message: e?.response?.data?.message || 'Ошибка отправки в 1С' });
      } finally {
        this.invSending1C = { ...this.invSending1C, [id]: false };
      }
    },

    async onDownloadAgreement(row) {
      const id = row.project_agreement_id;
      const previewWindow = this._preparePdfPreviewWindow();
      this.agrDownloading = { ...this.agrDownloading, [id]: true };
      try {
        const res = await unsignedSignAPI.downloadAgreement(id);
        this._openBlobInNewTab(res, previewWindow);
      } catch (e) {
        if (previewWindow && !previewWindow.closed) previewWindow.close();
        const message = await this._extractHttpErrorMessage(e, "Ошибка скачивания договора");
        this.$q.notify({ type: "negative", message });
      } finally {
        this.agrDownloading = { ...this.agrDownloading, [id]: false };
      }
    },

    async onSignAgreement(row) {
      const id = row.project_agreement_id;
      this.agrSigning = { ...this.agrSigning, [id]: true };
      try {
        const res = await unsignedSignAPI.signAgreement(id, this.buildReturnUrl());
        if (!res?.redirect_url) {
          this.$q.notify({ type: "negative", message: "Сервис подписи не вернул ссылку" });
          return;
        }
        if (isChrome()) window.open(res.redirect_url);
        else window.location.href = res.redirect_url;
      } catch (e) {
        this.$q.notify({ type: "negative", message: e?.response?.data?.error || "Ошибка создания процесса подписания" });
      } finally {
        this.agrSigning = { ...this.agrSigning, [id]: false };
      }
    },

    async onDownloadDocument(row) {
      const id = row.floor_map_document_id;
      const previewWindow = this._preparePdfPreviewWindow();
      this.docDownloading = { ...this.docDownloading, [id]: true };
      try {
        let res = null;
        if (row.all_signed) {
          const params = {
            project_id:          row.project_id          ?? undefined,
            sbs_project_id:      row.sbs_project_id      ?? undefined,
            project_entrance_id: row.project_entrance_id ?? undefined,
            project_type_id:     row.project_type_id     ?? undefined,
            resident_id:         row.resident_id         ?? undefined,
          };
          res = await unsignedSignAPI.downloadDocument(id, params);
        } else {
          res = await unsignedSignAPI.downloadDocumentByMasterUrl(row.master_url);
        }
        this._openBlobInNewTab(res, previewWindow);
        if (
          this.activeTab === "exec_docs"
          && parseFloorMapDocumentIdFromQuery(this.$route.query) === id
        ) {
          const hadStrip = this.stripUnsignedSignFloorMapDocumentIdFromRoute();
          if (hadStrip) this.$nextTick(() => this.loadDocuments(1));
        }
      } catch (e) {
        if (previewWindow && !previewWindow.closed) previewWindow.close();
        const message = await this._extractHttpErrorMessage(e, "Ошибка скачивания документа");
        this.$q.notify({ type: "negative", message });
      } finally {
        this.docDownloading = { ...this.docDownloading, [id]: false };
      }
    },

    async onDownloadK2svByBuhAvr(row) {
      const id = row.floor_map_document_id;
      const loadingKey = `k2sv_${id}`;
      const previewWindow = this._preparePdfPreviewWindow();
      this.docDownloading = { ...this.docDownloading, [loadingKey]: true };
      try {
        const res = await unsignedSignAPI.downloadK2svByBuhAvr(id);
        this._openBlobInNewTab(res, previewWindow);
      } catch (e) {
        if (previewWindow && !previewWindow.closed) previewWindow.close();
        const message = await this._extractHttpErrorMessage(e, "Ошибка скачивания КС-2В");
        this.$q.notify({ type: "negative", message });
      } finally {
        this.docDownloading = { ...this.docDownloading, [loadingKey]: false };
      }
    },

    async onSignDocument(row) {
      const id = row.floor_map_document_id;
      const isM19Over =
        row.floor_map_document_type_code === "M19_OVER" ||
        Number(row.floor_map_document_type_id) === FLOOR_MAP_DOC_TYPE_ID_M19_OVER;
      this.docSigning = { ...this.docSigning, [id]: true };
      try {
        const res = isM19Over
          ? await unsignedSignAPI.signM19OverDocument(id, this.buildReturnUrl())
          : await unsignedSignAPI.signDocument(id, this.buildReturnUrl());
        if (!res?.redirect_url) {
          this.$q.notify({ type: "negative", message: "Сервис подписи не вернул ссылку" });
          return;
        }
        if (isChrome()) window.open(res.redirect_url);
        else window.location.href = res.redirect_url;
      } catch (e) {
      } finally {
        this.docSigning = { ...this.docSigning, [id]: false };
      }
    },

    showAvrStornoButton(row) {
      if (!row) return false;
      const isAvr =
        Number(row.floor_map_document_type_id) === FLOOR_MAP_DOC_TYPE_ID_BUH_AVR
        || String(row.floor_map_document_type_code || "").toUpperCase() === "BUH_AVR";
      return isAvr && row.all_signed && row.is_avr_sent_bi;
    },

    isBuhGroupDeletable(group) {
      if (!group?.cards) return false;
      const rows = group.cards.map((c) => c.row).filter(Boolean);
      if (!rows.length) return false;
      return !rows.some((r) => r.is_avr_sent_bi);
    },

    async onDeleteBuhGroup(group) {
      const id = group.row.floor_map_document_id;
      const confirmed = await new Promise((resolve) => {
        this.$q.dialog({
          title: "Удалить группу документов?",
          message: "Будут удалены все документы группы: КС-2В, АВР, 3КС. Это действие необратимо.",
          cancel: { label: "Отмена", flat: true },
          ok: { label: "Удалить", color: "negative" },
          persistent: true,
        }).onOk(() => resolve(true)).onCancel(() => resolve(false));
      });
      if (!confirmed) return;
      this.docDeleting = { ...this.docDeleting, [id]: true };
      const res = await unsignedSignAPI.deleteBuhGroup(id);
      this.docDeleting = { ...this.docDeleting, [id]: false };
      if (!res) return;
      showSuccessToast("Документы удалены");
      await this.loadDocuments(undefined, "buh_docs");
    },

    async onStornoDocument(row) {
      const id = row.floor_map_document_id;
      const stornoDateMode = await new Promise((resolve) => {
        this.$q.dialog({
          title: "Сторнировать АВР?",
          message: "Выберите дату, которая уйдёт в УСП как дата сторно.",
          options: {
            type: "radio",
            model: "today",
            items: [
              { label: "Сегодняшняя дата и время", value: "today" },
              { label: "Дата документа", value: "document" },
            ],
          },
          cancel: { label: "Отмена", flat: true },
          ok: { label: "Сторнировать", color: "deep-orange" },
          persistent: true,
        }).onOk((value) => resolve(value || "today")).onCancel(() => resolve(null));
      });
      if (!stornoDateMode) return;

      this.docStornoing = { ...this.docStornoing, [id]: true };
      let shouldReload = false;
      try {
        await unsignedSignAPI.stornoDocument(id, stornoDateMode);
        shouldReload = true;
        showSuccessToast("Отсторнировано");
      } catch (e) {
        this.$q.notify({
          type: "negative",
          message: e?.response?.data?.error || e?.message || "Ошибка сторнирования",
        });
      } finally {
        this.docStornoing = { ...this.docStornoing, [id]: false };
        if (shouldReload) await this.loadDocuments(undefined, "buh_docs");
      }
    },

    // ── Массовое подписание документов ───────────────────────────────────
    toggleDocSelectAll(value) {
      if (value) {
        const newIds = [...new Set([...this.docSelectedIds, ...this.docSelectableIds])];
        this.docSelectedIds = newIds;
      } else {
        this.docSelectedIds = this.docSelectedIds.filter(
          (id) => !this.docSelectableIds.includes(id)
        );
      }
    },

    toggleDocSelectItem(id, value) {
      if (value) {
        if (!this.docSelectedIds.includes(id)) this.docSelectedIds.push(id);
      } else {
        this.docSelectedIds = this.docSelectedIds.filter((x) => x !== id);
      }
    },

    async onBatchSignDocuments() {
      if (this.docSelectedIds.length === 0) return;
      if (this.docSelectedIds.length > 10) {
        this.$q.notify({ type: "warning", message: "Максимум 10 документов за раз" });
        return;
      }
      if (parseFloorMapDocumentIdFromQuery(this.$route.query)) this.stripUnsignedSignFloorMapDocumentIdFromRoute();
      this.docBatchSigning = true;
      try {
        const res = await unsignedSignAPI.batchSignDocuments(this.docSelectedIds, this.buildReturnUrl());
        const signUrl = res?.data;
        if (!signUrl || typeof signUrl !== "string") {
          this.$q.notify({ type: "negative", message: "Сервис подписи не вернул ссылку" });
          return;
        }
        this.docSelectedIds = [];
        if (isChrome()) window.open(signUrl);
        else window.location.href = signUrl;
      } catch (e) {
        this.$q.notify({ type: "negative", message: e?.response?.data?.error || e?.message || "Ошибка массового подписания" });
      } finally {
        this.docBatchSigning = false;
      }
    },

    async loadFilterOptions() {
      const [providers, projectTypes, docTypes, contractors, agrContractors] = await Promise.allSettled([
        unsignedSignAPI.getProviders(),
        unsignedSignAPI.getProjectTypes(),
        unsignedSignAPI.getDocumentTypes(),
        unsignedSignAPI.getContractors(),
        unsignedSignAPI.getAgrContractors(),
      ]);
      if (providers.status       === 'fulfilled') this.providersOptions      = providers.value?.data       || providers.value       || [];
      if (projectTypes.status    === 'fulfilled') this.projectTypesOptions   = projectTypes.value?.data    || projectTypes.value    || [];
      if (docTypes.status        === 'fulfilled') this.docTypesOptions       = docTypes.value?.data        || docTypes.value        || [];
      if (contractors.status     === 'fulfilled') this.contractorsOptions    = contractors.value?.data     || contractors.value     || [];
      if (agrContractors.status  === 'fulfilled') this.agrContractorsOptions = agrContractors.value?.data  || agrContractors.value  || [];
    },
  },

  async mounted() {
    const tabFromUrl = parseTabFromQuery(this.$route.query);
    if (tabFromUrl) {
      this.activeTab = tabFromUrl;
    } else {
      await this.$router
        .replace({ query: { ...this.$route.query, tab: this.activeTab } })
        .catch(() => {});
    }

    if (this.activeTab !== "exec_docs" && parseFloorMapDocumentIdFromQuery(this.$route.query)) {
      const q = { ...this.$route.query };
      delete q.floor_map_document_id;
      await this.$router.replace({ query: q }).catch(() => {});
    }

    // Если в URL лежат фильтры (например, после возврата по return_url или ручного ввода) —
    // применяем их к state ДО первой загрузки. См. services/url-state.js.
    this.applyFiltersFromUrl(this.$route.query);

    const appStore = useAppStore();
    void fetchMenuStatuses().then((menuStatuses) => {
      if (menuStatuses) appStore.setMenuStatusBadges(menuStatuses);
    });
    void this.initLabelsFromUrl();

    await this.loadDocResidents();
    await this.initDocFiltersFromUrl();

    await Promise.all([
      this.loadFilterOptions(),
      this.loadInvoices(),
      this.loadAgreements(),
      this.loadDocuments(undefined, 'buh_docs'),
      this.loadDocuments(undefined, 'exec_docs'),
    ]);
  },
};
</script>

<style scoped>
.us-page {
  /* Палитра как в «Закрывающих документах» */
  --us-primary: #1976d2;
  --us-primary-dark: #1565c0;
  --us-bg-page: #ffffff;
  --us-surface: #ffffff;
  --us-surface-soft: #f8fafc;
  --us-border: #e2e8f0;
  --us-border-strong: #e2e8f0;
  --us-border-soft: #e2e8f0;
  --us-head-bg: #f8fafc;
  --us-call-bg: #f8fafc;
  --us-call-border: #e2e8f0;
  --us-doc-blue: #e3f2fd;
  --us-doc-beige: #fff8e1;
  --us-doc-green: #ecfdf5;
  --us-doc-placeholder: #f8fafc;
  --us-meta-bg: #f1f5f9;
  --us-meta-border: #e2e8f0;
  --us-muted-bg: #f1f5f9;
  --us-muted-text: #64748b;
  --us-text-primary: #1e293b;
  --us-text-secondary: #64748b;
  position: relative;
  margin: 0;
  min-height: calc(100vh - 72px);
  padding: 8px 24px 20px;
  margin-top: -4px;
  background: #fff;
  font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
}

.us-page__progress {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 3;
}

.us-page__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 0;
  padding-bottom: 2px;
  box-shadow: none;
}

.us-page__header-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.us-page__title {
  margin: 0;
  font-size: 20px;
  line-height: 1.25;
  font-weight: 700;
  color: #1e293b;
  letter-spacing: 0;
}

.us-page__header-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.us-page__header-actions :deep(.q-btn) {
  border: none;
  background: transparent;
  color: #64748b;
  transition: color 0.2s ease;
}

.us-page__header-actions :deep(.q-btn:hover) {
  color: #1565c0;
  background: transparent;
}

.us-shell {
  background: transparent;
  border: none;
  border-radius: 0;
  overflow: visible;
  box-shadow: none;
}

.us-tabs {
  background: transparent;
  padding: 0;
  border-bottom: 1px solid #e2e8f0;
}

.us-tabs__scroll {
  gap: 8px;
}

.us-tabs :deep(.q-tab) {
  min-height: 38px;
  height: 38px;
  padding: 0 8px;
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
  border-radius: 0;
  transition: color 0.2s ease, opacity 0.2s ease;
}

.us-tabs :deep(.q-tab:hover) {
  color: #334155;
}

.us-tabs :deep(.q-tab--active) {
  color: #1976d2;
  font-weight: 600;
}

.us-tabs :deep(.q-tab__indicator) {
  background: #1976d2 !important;
  height: 2px;
  border-radius: 0;
}

.us-tabs :deep(.q-tabs__arrow) {
  color: #94a3b8;
}

.us-panels {
  background: transparent;
}

.us-tab-panel {
  padding: 10px 0 0 !important;
}

.us-tab-panel--docs {
  padding-top: 0 !important;
}

/* Фильтры БУХ / M19 — как в «Закрывающих документах» (стандартный Quasar outlined) */
.us-doc-controls-band {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0 -24px 10px;
  padding: 0 24px 4px;
  flex-shrink: 0;
}

.us-doc-filters-row {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  flex-wrap: wrap;
  margin: 0;
  padding: 6px 10px 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: none;
}

.us-doc-filters {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  flex: 1;
  min-width: 0;
  align-items: flex-end;
}

.us-doc-reset-btn {
  flex: 0 0 auto;
  height: 40px;
  min-height: 40px;
  align-self: flex-end;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
}

.cd-filter {
  flex: 1;
  min-width: 100px;
}

.cd-filter--sm {
  min-width: 140px;
  max-width: 200px;
  flex: 1;
}

.cd-filter--entrance {
  min-width: 220px;
  max-width: 360px;
  flex: 1 1 280px;
}

.cd-filter--level {
  min-width: 100px;
  max-width: 150px;
  flex: 0 1 130px;
}

.cd-filter--md {
  min-width: 168px;
  max-width: 220px;
  flex: 0 1 200px;
}

.us-page .us-doc-controls-band :deep(.cd-filter.q-field--outlined .q-field__control) {
  min-height: 40px;
  height: 40px;
  border-radius: 4px;
  border: none;
  background: #fff;
  box-shadow: none;
}

.us-page .us-doc-controls-band :deep(.cd-filter.q-field--outlined .q-field__control:before) {
  border: 1px solid rgba(0, 0, 0, 0.24) !important;
  border-radius: 4px;
}

.us-page .us-doc-controls-band :deep(.cd-filter.q-field--outlined .q-field__control:after) {
  border-radius: 4px;
}

.us-page .us-doc-controls-band :deep(.cd-filter.q-field--focused .q-field__control) {
  border: none;
  box-shadow: none;
}

.us-page .us-doc-controls-band :deep(.cd-filter .q-field__label) {
  font-size: 12px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.6);
}

.us-page .us-doc-controls-band :deep(.cd-filter .q-field__native),
.us-page .us-doc-controls-band :deep(.cd-filter .q-field__input) {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
}

.us-page .us-doc-controls-band :deep(.cd-filter.q-field--outlined.q-field--disabled .q-field__control) {
  background: rgba(0, 0, 0, 0.04);
}

.us-page .us-doc-controls-band .cd-filter--md :deep(.q-field__control) {
  min-height: 40px;
  height: auto;
  padding-top: 2px;
  padding-bottom: 2px;
}

.us-page .us-doc-controls-band .cd-filter--md :deep(.q-field__native),
.us-page .us-doc-controls-band .cd-filter--md :deep(.q-field__input) {
  display: flex;
  align-items: center;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  min-height: 22px;
}

.us-page .us-doc-controls-band .cd-filter--md :deep(.q-field__label) {
  font-size: 12px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.6);
}

.us-filters-shell {
  background: #f8fafc;
  border-radius: 10px;
  padding: 6px 10px 8px;
  border: 1px solid #e2e8f0;
  box-shadow: none;
  margin-bottom: 10px;
}

.us-filters-shell__title {
  display: none;
}

.us-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 10px;
}

.us-filters--exec {
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 2px;
}

.us-filters--exec .us-filters__btn {
  margin-left: 0;
  flex: 0 0 auto;
}

.us-filters__item {
  min-width: 132px;
  flex: 1 1 132px;
}

.us-filters__item--sm {
  flex: 0 1 160px;
  min-width: 140px;
  max-width: 200px;
}

.us-filters__item--entrance {
  flex: 1 1 220px;
  min-width: 180px;
  max-width: 320px;
}

.us-filters__item--wide {
  min-width: 240px;
  flex: 1.35 1 280px;
}

.us-filters__item--narrow {
  min-width: 96px;
  max-width: 132px;
  flex: 1 1 96px;
}

.us-filters__item--doc-types {
  min-width: 180px;
  flex: 1.12 1 210px;
}

.us-filters__item--exec-project {
  min-width: 150px;
  max-width: 210px;
  flex: 0 1 200px;
}

.us-filters__item--exec-block {
  min-width: 110px;
  max-width: 150px;
  flex: 0 1 140px;
}

.us-filters__item--exec-contractor {
  min-width: 130px;
  max-width: 180px;
  flex: 0 1 170px;
}

.us-filters__item--audit-multi {
  min-width: 198px;
  max-width: 252px;
  flex: 0 1 228px;
}

.us-filters__item--audit-multi :deep(.q-field__control) {
  min-height: 38px;
  padding-top: 0;
  padding-bottom: 0;
}

.us-filters__item--audit-multi :deep(.q-field__native),
.us-filters__item--audit-multi :deep(.q-field__input) {
  display: flex;
  align-items: center;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.us-filters__item--audit-multi :deep(.q-field__label) {
  font-size: 12px;
  font-weight: 600;
  color: #667085;
}

.us-audit-chip {
  margin-right: 4px;
  margin-top: 2px;
  margin-bottom: 2px;
  font-size: 11px;
  font-weight: 600;
}
.us-filters__item--date {
  min-width: 110px;
  max-width: 130px;
}

.us-filters__btn {
  height: 40px;
  border-radius: 4px;
  margin-left: auto;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.us-reset-btn {
  border-color: #e2e8f0 !important;
  color: #64748b !important;
  background: #fff !important;
}

.us-reset-btn:hover {
  border-color: #cbd5e1 !important;
  color: #334155 !important;
  background: #f8fafc !important;
}

.us-filters__row-break {
  flex-basis: 100%;
  height: 0;
}

.us-page :deep(.custom-select__label) {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 6px;
}

.us-page .us-shell :deep(.q-field--outlined .q-field__control),
.us-page .us-filters-shell :deep(.q-field--outlined .q-field__control) {
  min-height: 40px;
  height: 40px;
  border-radius: 4px;
  border: none;
  background: #fff;
  box-shadow: none;
}

.us-page .us-shell :deep(.q-field--outlined .q-field__control:before),
.us-page .us-filters-shell :deep(.q-field--outlined .q-field__control:before) {
  border: 1px solid rgba(0, 0, 0, 0.24) !important;
  border-radius: 4px;
}

.us-page .us-shell :deep(.q-field--outlined .q-field__control:after),
.us-page .us-filters-shell :deep(.q-field--outlined .q-field__control:after) {
  border-radius: 4px;
}

.us-page .us-shell :deep(.q-field__native),
.us-page .us-shell :deep(.q-field__input),
.us-page .us-shell :deep(.q-field__prefix),
.us-page .us-shell :deep(.q-field__suffix),
.us-page .us-filters-shell :deep(.q-field__native),
.us-page .us-filters-shell :deep(.q-field__input),
.us-page .us-filters-shell :deep(.q-field__prefix),
.us-page .us-filters-shell :deep(.q-field__suffix) {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
}

.us-page .us-shell :deep(.q-field__native::placeholder),
.us-page .us-shell :deep(.q-field__input::placeholder),
.us-page .us-filters-shell :deep(.q-field__native::placeholder),
.us-page .us-filters-shell :deep(.q-field__input::placeholder) {
  color: #94a3b8;
}

.us-page .us-shell :deep(.q-field--focused .q-field__control),
.us-page .us-filters-shell :deep(.q-field--focused .q-field__control) {
  border: none;
  box-shadow: none;
}

.us-page .us-shell :deep(.q-field--outlined.q-field--disabled .q-field__control),
.us-page .us-filters-shell :deep(.q-field--outlined.q-field--disabled .q-field__control) {
  background: rgba(0, 0, 0, 0.04);
  border-color: transparent;
}

.us-page .us-shell :deep(.q-field__label),
.us-page .us-filters-shell :deep(.q-field__label) {
  font-size: 12px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.6);
}

.us-page .us-shell :deep(.q-chip),
.us-page .us-filters-shell :deep(.q-chip) {
  border-radius: 999px;
  min-height: 22px;
  font-size: 11px;
}

.us-doc-type-qselect :deep(.q-field__control) {
  padding-right: 6px;
}

.us-table-card {
  --us-col-project: 240px;
  --us-col-contractor: 220px;
  --us-col-docs: minmax(700px, 1fr);
  --us-table-min-width: 1160px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #fff;
  box-shadow: none;
  overflow: hidden;
}

.us-table-card--docs {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.us-table-scroll-host {
  width: 100%;
  max-width: 100%;
  min-height: 760px;
  height: clamp(760px, calc(100dvh - 80px), 1200px);
  max-height: none;
  overflow: auto;
  background: #fff;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
}

.us-table-scroll-host > .us-journal-grid-head,
.us-table-scroll-host > .us-table-toolbar,
.us-table-scroll-host > .us-doc-list {
  width: 100%;
  min-width: var(--us-table-min-width);
}

.us-table-scroll-host > .us-journal-grid-head {
  position: sticky;
  top: 0;
  z-index: 20;
}

.us-journal-grid-head {
  display: grid;
  grid-template-columns: var(--us-col-project) var(--us-col-contractor) var(--us-col-docs);
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
}

.us-journal-grid-head__cell {
  display: flex;
  align-items: center;
  min-height: 40px;
  padding: 8px 12px;
  border-right: 1px solid #e2e8f0;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
}

.us-journal-grid-head__title {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}

.us-journal-grid-head__cell--project {
  align-items: flex-start;
  justify-content: center;
  flex-direction: column;
  gap: 2px;
}

.us-select-all-head {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 18px;
}

.us-select-all-head :deep(.q-checkbox__label) {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
}

.us-select-all-head :deep(.q-checkbox__inner) {
  color: #1976d2;
}

.us-count--head {
  font-size: 11px;
  white-space: nowrap;
}

.us-journal-grid-head:not(.us-journal-grid-head--buh) .us-journal-grid-head__cell--project {
  min-height: 52px;
}

.us-journal-grid-head__cell:last-child {
  border-right: none;
  background: #f8fafc;
}

.us-journal-grid-head--buh {
  grid-template-columns: var(--us-col-project) var(--us-col-contractor) var(--us-col-docs);
  grid-template-rows: 36px 30px;
  grid-template-areas:
    "project contractor docs"
    "project contractor docTypes";
}

.us-journal-grid-head--buh .us-journal-grid-head__cell {
  min-height: 0;
}

.us-journal-grid-head--buh .us-journal-grid-head__cell--project {
  grid-area: project;
}

.us-journal-grid-head--buh .us-journal-grid-head__cell--contractor {
  grid-area: contractor;
}

.us-journal-grid-head--buh .us-journal-grid-head__cell--docs {
  grid-area: docs;
  border-right: none;
  border-bottom: 1px solid var(--us-border-strong);
}

.us-journal-grid-head__doc-types {
  grid-area: docTypes;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  border-left: 1px solid var(--us-border-strong);
}

.us-journal-grid-head__doc-type {
  display: flex;
  align-items: center;
  min-height: 30px;
  padding: 0 10px;
  border-right: 1px solid #e2e8f0;
  color: #64748b;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.us-journal-grid-head__doc-type:last-child {
  border-right: none;
}

.us-journal-grid-head__doc-type--blue {
  background: #e3f2fd;
  color: #1565c0;
}

.us-journal-grid-head__doc-type--beige {
  background: #fff8e1;
  color: #92400e;
}

.us-journal-grid-head__doc-type--green {
  background: #ecfdf5;
  color: #047857;
}

.us-table-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: 8px 12px;
  border-bottom: 1px dashed #e2e8f0;
  background: #f8fafc;
}

.us-table-toolbar :deep(.q-checkbox__label) {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}

.us-table-toolbar :deep(.q-checkbox__inner) {
  color: #1976d2;
}

.us-batch-sign-btn {
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0;
}

.us-count {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}

.us-doc-list {
  position: relative;
  padding: 0;
  background: #ffffff;
}

.us-empty-state {
  padding: 28px 12px;
  text-align: center;
  color: #94a3b8;
  font-size: 14px;
}

.us-call-group {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  height: 28px;
  padding: 0 12px;
  margin: 0;
  border-top: 1px solid #e2e8f0;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
}

.us-row-card {
  background: var(--us-surface);
  border-radius: 0;
  border: none;
  border-bottom: 1px solid var(--us-border-soft);
  overflow: hidden;
  margin-bottom: 0;
  box-shadow: none;
  transition: background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}

.us-row-card:last-child {
  border-bottom: none;
}

.us-row-card:hover {
  transform: none;
  box-shadow: none;
  background: #f8fafc;
  border-bottom-color: #e2e8f0;
}

.us-row-card__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  background: #fff;
  padding: 8px 12px;
  border-bottom: 1px dashed #e2e8f0;
}

.us-row-card__title-wrap {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
  max-width: 100%;
}

.us-row-card__title {
  margin: 0;
  font-size: 13px;
  line-height: 1.3;
  color: var(--us-text-primary);
  font-weight: 600;
  max-width: 100%;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.us-row-card__date {
  color: var(--us-text-secondary);
  font-size: 12px;
  white-space: nowrap;
}

.us-id-chip {
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 8px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 10px;
  font-weight: 600;
}

.us-row-grid {
  display: grid;
  grid-template-columns: var(--us-col-project) var(--us-col-contractor) var(--us-col-docs);
  gap: 0;
  padding: 0;
  min-width: 0;
}

.us-grid-col {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border-right: 1px solid var(--us-border-soft);
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}

.us-grid-col:last-child {
  border-right: none;
}

.us-doc-card-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.us-row-card--buh:hover {
  background: #f8fafc;
}

.us-row-grid--buh .us-grid-col {
  padding: 10px 12px;
}

.us-row-grid--buh .us-grid-col__label {
  display: none;
}

.us-row-grid--buh .us-grid-col--docs {
  padding: 0;
  gap: 0;
  width: 100%;
  min-width: 0;
}

.us-doc-card-grid--buh {
  gap: 0;
  min-height: 100%;
  width: 100%;
  min-width: 0;
}

.us-doc-card-grid--buh .us-doc-mini-card {
  min-height: 166px;
  border-radius: 0;
  border: none;
  border-right: 1px solid var(--us-border);
}

.us-doc-card-grid--buh .us-doc-mini-card:last-child {
  border-right: none;
}

.us-grid-col__label {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.proj-link {
  display: block;
  max-width: 100%;
  color: #1565c0;
  text-decoration: none;
  font-size: 13px;
  line-height: 1.4;
  font-weight: 500;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.proj-link:hover {
  color: #0d47a1;
  text-decoration: underline;
}

.us-meta-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-width: 100%;
  min-width: 0;
}

.us-meta-tag {
  display: inline-block;
  max-width: 100%;
  min-height: 20px;
  padding: 1px 6px;
  border-radius: 4px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 10px;
  font-weight: 500;
  line-height: 1.35;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  border: 1px solid #e2e8f0;
}

.us-project-works {
  font-size: 12px;
  font-weight: 500;
  color: #1e293b;
  line-height: 1.3;
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.us-project-works__label {
  color: #64748b;
  font-weight: 600;
}

.us-project-works__value {
  color: #1e293b;
  font-weight: 500;
}

.us-doc-mini-card {
  min-height: 128px;
  width: 100%;
  border-radius: 8px;
  padding: 10px;
  position: relative;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  min-width: 0;
  overflow: hidden;
  transition: background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}

.us-doc-mini-card:hover {
  transform: none;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  border-color: #cbd5e1;
}

.us-doc-mini-card--buh {
  padding: 9px 10px 8px;
}

.us-doc-mini-card--buh:hover {
  box-shadow: none;
  border-color: transparent;
}

.us-doc-mini-card--blue {
  background: var(--us-doc-blue);
}

.us-doc-mini-card--beige {
  background: var(--us-doc-beige);
}

.us-doc-mini-card--green {
  background: var(--us-doc-green);
}

.us-doc-mini-card--placeholder {
  border-style: dashed;
  border-color: #94a3b8;
  background: #f8fafc;
}

.us-doc-card-grid--buh .us-doc-mini-card--placeholder {
  border-right: 1px dashed #cbd5e1;
}

.us-doc-mini-card__head {
  display: flex;
  justify-content: space-between;
  gap: 6px;
  min-width: 0;
  align-items: flex-start;
}

.us-doc-mini-card__title-wrap {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  min-width: 0;
}

.us-doc-mini-card__title {
  margin: 0;
  font-size: 13px;
  line-height: 1.35;
  color: #1e293b;
  font-weight: 600;
  max-width: 100%;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.us-doc-mini-card__date {
  margin: 2px 0 0;
  font-size: 11px;
  color: #64748b;
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.us-doc-mini-card__badges {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  justify-content: flex-end;
  align-content: flex-start;
  align-items: center;
  min-width: 0;
  max-width: 48%;
}

.us-badge {
  height: 20px;
  padding: 0 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  border: 1px solid transparent;
}

.us-badge--signed {
  background: #ecfdf5;
  color: #047857;
  border-color: #a7f3d0;
}

.us-badge--esf {
  background: #e3f2fd;
  color: #1565c0;
  border-color: #bbdefb;
}

.us-badge--esf-no {
  background: #fff4ec;
  color: #c25d18;
  border-color: #ffdcbf;
}

.us-badge--muted {
  background: #f1f5f9;
  color: #64748b;
  border-color: #e2e8f0;
}

.us-k2sv-download-btn {
  min-height: 24px;
  height: 24px;
  padding: 0 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.86);
  color: #0f5ea8;
  border: 1px solid rgba(37, 99, 235, 0.2);
  box-shadow: 0 1px 2px rgba(15, 94, 168, 0.08);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.us-k2sv-download-btn:hover {
  background: #ffffff;
  border-color: rgba(37, 99, 235, 0.36);
  color: #0b4f91;
}

.us-info-block {
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px;
  margin-top: 8px;
}

.us-info-line {
  font-size: 11px;
  color: #64748b;
  line-height: 1.35;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.us-info-line + .us-info-line {
  margin-top: 4px;
}

.us-info-line--clickable {
  cursor: pointer;
  color: #1565c0;
  font-weight: 500;
}

.us-doc-mini-card__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.us-doc-mini-card__actions--inline {
  margin-top: 6px;
}

.us-storno-btn {
  height: 28px;
  border-radius: 6px;
  padding: 0 12px;
  font-size: 12px;
  font-weight: 700;
}

.us-sign-btn {
  background: #1976d2;
  color: #fff;
  border-radius: 4px;
  height: 28px;
  padding: 0 12px;
  font-size: 12px;
  font-weight: 500;
  border: none;
  box-shadow: none;
  transition: background-color 0.2s ease;
}

.us-sign-btn:hover {
  background: #1565c0;
  transform: none;
  box-shadow: none;
}

.us-ghost-btn {
  height: 28px;
  border-radius: 4px;
  padding: 0 10px;
  font-size: 11px;
  font-weight: 500;
  background: #e3f2fd;
  color: #1565c0;
  border: 1px solid #bbdefb;
}

.doc-cell-contractor {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  max-width: 100%;
}

.doc-contractor-name {
  display: block;
  max-width: 100%;
  color: #1e293b;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.35;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.doc-contractor-empty {
  color: #94a3b8;
  font-size: 13px;
}

.doc-signers {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.doc-signers--in-card {
  margin-top: 8px;
  gap: 4px;
}

.doc-signer {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 6px 8px;
  border-radius: 8px;
  border: 1px solid var(--us-border);
  border-left: 3px solid transparent;
  background: #ffffff;
  transition: border-color 0.2s ease;
}

.doc-signer:hover {
  border-color: #cbd5e1;
  box-shadow: none;
}

.doc-signer--done {
  background: #ecfdf5;
  border-left-color: #047857;
}

.doc-signer--waiting {
  background: #f8fafc;
  border-left-color: #f59e0b;
}

.doc-signer--me {
  background: #e3f2fd;
  border-left-color: #1976d2;
}

.doc-signer__icon {
  margin-top: 1px;
  flex-shrink: 0;
}

.doc-signer__icon--done {
  color: #047857;
}

.doc-signer__icon--wait {
  color: #94a3b8;
}

.doc-signer__info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.doc-signer__role {
  color: #64748b;
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.doc-signer__fio {
  color: #1e293b;
  font-size: 12px;
  line-height: 1.35;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.doc-signer__fio--done {
  color: #047857;
  font-weight: 600;
}

.doc-signer__company {
  color: #64748b;
  font-size: 11px;
}

.doc-signer__date {
  color: #64748b;
  font-size: 10px;
  display: inline-flex;
  align-items: center;
  gap: 3px;
}

.doc-signer__me-badge {
  height: 18px;
  padding: 0 6px;
  border-radius: 999px;
  background: #e3f2fd;
  color: #1565c0;
  font-size: 9px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

.doc-signers--in-card .doc-signer {
  padding: 5px 7px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.82);
}

.doc-signers--in-card .doc-signer__role {
  font-size: 9px;
}

.doc-signers--in-card .doc-signer__fio {
  font-size: 11px;
}

.doc-prorab-k2sv-line {
  margin-top: 8px;
  font-size: 10px;
  line-height: 1.4;
  color: #64748b;
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.us-pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px 12px;
  border-top: 1px solid var(--us-border);
  background: #f8fafc;
}

.inv-badges-row {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 5px;
}

.inv-badge {
  font-size: 10px;
  padding: 2px 5px;
  border-radius: 4px;
  white-space: nowrap;
}

/* ── Накладные: ячейка "Стороны" ── */
.inv-parties {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.inv-party-row {
  display: flex;
  align-items: baseline;
  gap: 5px;
  font-size: 12px;
}
.inv-party-role-label {
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 600;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
}
.inv-party-name {
  color: #333;
  word-break: break-word;
}

/* ── Накладные: ячейка "Статус / Действие" ── */
.inv-status-btn {
  font-size: 11px;
  border-radius: 12px;
  padding: 4px 8px;
  min-height: 24px;
  transition: filter 0.15s;
  white-space: normal;
  text-align: center;
}
.inv-status-btn :deep(.q-btn__content) {
  flex-direction: column;
  align-items: center;
  gap: 2px;
}
.inv-status-btn:hover {
  filter: brightness(0.92);
}
.inv-status-btn--waiting {
  background: #fff3e0;
  color: #e65100;
}
.inv-status-btn--pending-approval {
  background: #fff8e1;
  color: #6d4c00;
  border: 1px solid #ffe082;
}
.inv-status-btn--rejected-approval {
  background: #ffebee;
  color: #b71c1c;
  border: 1px solid #ef9a9a;
}
.inv-status-btn--can-sign {
  background: #1976d2;
  color: #ffffff;
}
.inv-status-btn--signed {
  background: #e8f5e9;
  color: #2e7d32;
}
.inv-status-btn--failed {
  background: #fce4ec;
  color: #b71c1c;
}
.inv-status-text {
  word-spacing: 100vw;
  line-height: 1.5;
  text-align: center;
  display: inline-block;
}

/* ── Договора: тип договора ── */
.agr-type-badge {
  font-size: 10px !important;
  padding: 1px 6px !important;
  border-radius: 8px !important;
  background: #e3f2fd !important;
  color: #1565c0 !important;
}

/* ── Договора: ячейка "Подписант" ── */
.agr-signer {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.agr-signer__role {
  align-self: flex-start;
  white-space: nowrap !important;
}
.agr-signer__fio {
  font-size: 13px;
  font-weight: 500;
  color: #333;
  line-height: 1.3;
}
.agr-signer__company {
  font-size: 11px;
  color: #777;
  line-height: 1.2;
}

/* ── Договора: дата подписания ── */
.agr-sign-date {
  font-size: 10px;
  color: #888;
  margin-top: 3px;
  white-space: nowrap;
}
.agr-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
}

/* ── Документы: ячейка "Проект" ── */
.doc-cell-project {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.doc-project-name {
  font-size: 13px;
  font-weight: 600;
  color: #1a2332;
  line-height: 1.3;
}
.doc-project-type-badge {
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  color: #5c6bc0;
  background: #e8eaf6;
  border-radius: 3px;
  padding: 1px 5px;
  line-height: 1.5;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  align-self: flex-start;
}
.doc-project-meta {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-top: 1px;
}
.doc-project-tag {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background: #f5f5f5;
  border-radius: 3px;
  padding: 1px 5px;
  font-size: 11px;
  color: #555;
}
.doc-project-tag__label {
  font-size: 10px;
  font-weight: 600;
  color: #999;
  text-transform: uppercase;
}
.doc-project-tag__val {
  color: #444;
}

/* ── Документы: ячейка "Документ" ── */
.doc-cell-docinfo {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.doc-doctype {
  font-size: 13px;
  font-weight: 600;
  color: #1a73e8;
  line-height: 1.3;
}
.doc-create-date {
  font-size: 11px;
  color: #888;
  display: flex;
  align-items: center;
}
.doc-sum {
  font-size: 12px;
  font-weight: 600;
  color: #2e7d32;
}
.doc-1c-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 4px;
  padding-top: 4px;
  border-top: 1px dashed #e0e0e0;
}
.doc-1c-row {
  font-size: 10px;
  color: #555;
  line-height: 1.4;
}
.doc-1c-sent {
  color: #2e7d32;
  font-weight: 600;
  display: flex;
  align-items: center;
}
.doc-1c-guid {
  word-break: break-all;
}
.doc-1c-label {
  color: #888;
  font-weight: 600;
}

/* ── Документы: ячейка "Подписанты" ── */
.doc-signers {
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
  overflow: hidden;
}
.doc-signer {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 4px 6px 4px 8px;
  border-radius: 5px;
  border-left: 3px solid transparent;
  min-width: 0;
  overflow: hidden;
}
.doc-signer--done {
  background: #f1f9f1;
  border-left-color: #4caf50;
}
.doc-signer--waiting {
  background: #fffbf0;
  border-left-color: #ffca28;
}
.doc-signer--me {
  border-left-color: #1a73e8;
  background: #f0f6ff;
}
.doc-signer__icon {
  flex-shrink: 0;
  margin-top: 1px;
}
.doc-signer__icon--done {
  color: #43a047;
}
.doc-signer__icon--wait {
  color: #ccc;
}
.doc-signer__info {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  gap: 1px;
}
.doc-signer__role {
  font-size: 10px;
  font-weight: 700;
  color: #9e9e9e;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  line-height: 1.2;
  word-break: break-word;
  overflow-wrap: break-word;
}
.doc-signer__fio {
  font-size: 12px;
  color: #555;
  line-height: 1.3;
}
.doc-signer__fio--done {
  color: #2e7d32;
  font-weight: 500;
}
.doc-signer__date {
  font-size: 10px;
  color: #888;
  display: flex;
  align-items: center;
  gap: 2px;
  margin-top: 1px;
}
.doc-signer__me-badge {
  font-size: 9px;
  font-weight: 700;
  color: #1a73e8;
  background: #e3f2fd;
  border-radius: 3px;
  padding: 1px 4px;
  line-height: 1.4;
  flex-shrink: 0;
  align-self: flex-start;
}

/* ── Документы: ячейка "Статус / Действие" ── */
.doc-status-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
}
.doc-status-cell__row {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
}
.doc-action-esf {
  flex-shrink: 0;
}
.doc-action-esf--row {
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 3.4rem;
  padding: 2px 6px;
  border-radius: 4px;
  background: #f5f5f5;
  font-size: 11px;
  font-weight: 600;
  color: #2e7d32;
}
.doc-action-esf__chk {
  color: #43a047;
  flex-shrink: 0;
}
.doc-sign-btn {
  width: 100%;
  min-width: 80px;
}
.doc-sign-btn--with-esf {
  width: auto;
  flex: 1 1 72px;
  min-width: 72px;
  max-width: 160px;
}
.doc-status-badge {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 2px;
  padding: 4px 8px;
}

.us-pagination :deep(.q-btn) {
  border-radius: 6px;
  min-width: 26px;
  height: 26px;
  font-size: 12px;
  font-weight: 600;
}

.entrance-tree-node {
  display: flex;
  align-items: center;
  padding: 3px 6px;
  border-radius: 4px;
  cursor: default;
  font-size: 13px;
  user-select: none;
}

.entrance-tree-node--project {
  font-weight: 600;
  color: #333;
}

.entrance-tree-node--entrance {
  cursor: pointer;
  color: #555;
}

.entrance-tree-node--entrance:hover {
  background: #e3f2fd;
  color: #1976d2;
}

.entrance-tree-node--selected {
  background: #1976d2 !important;
  color: #fff !important;
}

@media (max-width: 1280px) {
  .us-table-card {
    --us-col-project: 220px;
    --us-col-contractor: 200px;
    --us-col-docs: minmax(620px, 1fr);
    --us-table-min-width: 1040px;
  }

  .us-doc-card-grid:not(.us-doc-card-grid--buh) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 920px) {
  .us-page {
    padding: 14px;
  }

  .us-page__title {
    font-size: 20px;
  }

  .us-page__header {
    flex-direction: column;
    align-items: flex-start;
  }

  .us-tab-panel {
    padding: 8px 0 0 !important;
  }

  .us-journal-grid-head {
    display: none;
  }

  .us-filters,
  .us-doc-filters {
    gap: 8px;
  }

  .us-doc-filters-row {
    flex-direction: column;
    align-items: stretch;
  }

  .us-doc-filters .cd-filter,
  .us-doc-filters .cd-filter--sm,
  .us-doc-filters .cd-filter--entrance,
  .us-doc-filters .cd-filter--level,
  .us-doc-filters .cd-filter--md {
    min-width: 0;
    max-width: none;
    flex: 1 1 100%;
  }

  .us-doc-reset-btn {
    width: 100%;
  }

  .us-table-scroll-host {
    max-height: none;
    overflow: visible;
  }

  .us-table-scroll-host > .us-journal-grid-head,
  .us-table-scroll-host > .us-table-toolbar,
  .us-table-scroll-host > .us-doc-list {
    width: 100%;
    min-width: 0;
  }

  .us-filters--exec {
    flex-wrap: wrap;
    overflow: visible;
  }

  .us-filters__item,
  .us-filters__item--wide,
  .us-filters__item--doc-types,
  .us-filters__item--audit-multi,
  .us-filters__item--narrow,
  .us-filters__item--date {
    min-width: 100%;
    max-width: none;
    flex: 1 1 100%;
  }

  .us-table-toolbar {
    padding: 8px;
  }

  .us-row-grid {
    grid-template-columns: 1fr;
  }

  .us-grid-col {
    border-right: none;
    border-bottom: 1px solid #e2e8f0;
  }

  .us-grid-col:last-child {
    border-bottom: none;
  }

  .us-doc-card-grid {
    grid-template-columns: 1fr;
  }

  .us-row-grid--buh .us-grid-col__label {
    display: block;
  }

  .us-row-grid--buh .us-grid-col--docs {
    padding: 10px;
  }

  .us-doc-card-grid--buh {
    gap: 8px;
  }

  .us-doc-card-grid--buh .us-doc-mini-card {
    border: 1px solid #e2e8f0;
    border-radius: 8px;
  }

  .us-doc-card-grid--buh .us-doc-mini-card,
  .us-doc-card-grid--buh .us-doc-mini-card:last-child,
  .us-doc-card-grid--buh .us-doc-mini-card--placeholder {
    border-right: 1px solid #e2e8f0;
  }

  .us-row-card__head {
    align-items: flex-start;
    flex-direction: column;
  }

  .us-row-card {
    margin-bottom: 8px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
  }
}

@media (max-width: 640px) {
  .us-page__title {
    font-size: 18px;
  }

  .us-tabs {
    padding: 0 8px;
  }

  .us-tabs :deep(.q-tab) {
    font-size: 12px;
  }

  .us-row-card {
    border-radius: 14px;
  }

  .us-doc-list {
    padding: 0;
  }

  .us-row-grid {
    padding: 0;
  }

  .us-sign-btn,
  .us-ghost-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>

<style lang="scss">
.us-doc-audit-popup {
  padding: 0;
  min-width: 312px;
  max-width: 348px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #fff;
  box-shadow: 0 16px 36px rgba(15, 23, 42, 0.14);
}

.us-doc-audit-popup .q-virtual-scroll__content,
.us-doc-audit-popup .q-scrollarea__content {
  padding: 8px 10px 10px;
  background: #fff;
}

.us-doc-audit-popup .us-doc-audit-option {
  min-height: 44px;
  border-radius: 8px;
  margin: 0 0 6px;
  padding: 6px 10px;
  position: relative;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
}

.us-doc-audit-popup .us-doc-audit-option--esf-group-end {
  margin-bottom: 14px;
  padding-bottom: 2px;
}

.us-doc-audit-popup .us-doc-audit-option--sign-group-start {
  margin-top: 4px !important;
  padding-top: 14px !important;
  border-top: 2px solid #cbd5e1 !important;
}

.us-doc-audit-popup .us-doc-audit-option--esf {
  border-left: 3px solid #1976d2;
}

.us-doc-audit-popup .us-doc-audit-option--sign {
  border-left: 3px solid #64748b;
}

.us-doc-audit-popup .us-doc-audit-option--selected {
  background: #e3f2fd;
  border-color: #bbdefb;
  box-shadow: 0 0 0 1px rgba(25, 118, 210, 0.12);
}

.us-doc-audit-popup .us-doc-audit-option__icon {
  color: #94a3b8;
}

.us-doc-audit-popup .us-doc-audit-option__icon--selected {
  color: #1976d2;
}

.us-doc-audit-popup .us-doc-audit-option__icon--esf {
  color: #1565c0;
}

.us-doc-audit-popup .us-doc-audit-option__icon--sign {
  color: #64748b;
}

.us-doc-audit-popup .us-doc-audit-option__label {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  min-width: 0;
}

.us-doc-audit-popup .us-doc-audit-option--esf .us-doc-audit-option__label {
  color: #1565c0;
}

.us-doc-audit-popup .us-doc-audit-option--sign .us-doc-audit-option__label {
  color: #1e293b;
}
</style>
